import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Card,
  CardBody,
  Col,
  Container,
  Form,
  FormGroup,
  Label,
  Row,
  Button,
  Input,
  Table,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";
import Select, { components } from "react-select";
import { ClipLoader } from "react-spinners";
import Swal from "sweetalert2";
import { getCustomers, searchCustomers } from "../../components/Api/GmsCustomer";
import { getEmployees } from "../../components/Api/GmsEmployee";
import { getRooms } from "../../components/Api/GmsRoom";
import { saveBooking, /* checkAvailability, checkAvailabilityForTime, */ signBookingConsent } from "../../components/Api/GmsBooking";
import { getSpaServices } from "../../components/Api/GmsSpaService";
import { getSpaPackages, getSpaPackageById } from "../../components/Api/GmsSpaPackage";
import { getSpaPackageSeries } from "../../components/Api/GmsSpaPackageSeries";
import { getCustomerMemberships } from "../../components/Api/GmsCustomerMembership";
import { getCustomerBilledStandaloneServices } from "../../components/Api/GmsBooking";
import { getEquipments } from "../../components/Api/GmsEquipment";
import { getSources } from "../../components/Api/GmsSource";
import { getBusinesses } from "../../components/Api/GmsBusiness";
import { getBranches } from "../../components/Api/GmsBranch";
import { Calendar, User, X, Search } from "lucide-react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Breadcrumb from "../../components/Common/Breadcrumb";
import { useLocation, useNavigate } from "react-router-dom";
import QuickAddCustomer from "../../components/QuickAddCustomer";
import CustomerProfileModal from "../../components/CustomerProfileModal";
import ConsentFormModal from "../../components/ConsentFormModal";
import CustomerAdvancedSearchModal from "../../components/CustomerAdvancedSearchModal";
import {
  hasPagePermissionWithAdmin,
  hasViewOnlyPermission,
  hasEditOrHigherPermission,
  isUserAdmin
} from "../../components/Permission/PermissionService";

// Custom styles for Select components (from PayrollProcessing)
const selectStyles = {
  control: (provided, state) => ({
    ...provided,
    minHeight: '38px',
    borderColor: state.isFocused ? '#556ee6' : '#ced4da',
    boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(85, 110, 230, 0.25)' : 'none',
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 9999,
    maxHeight: 200,
  }),
  menuList: (provided) => ({
    ...provided,
    maxHeight: 180,
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? '#556ee6' : state.isFocused ? '#f8f9fa' : 'white',
    color: state.isSelected ? 'white' : '#495057',
    '&:hover': {
      backgroundColor: state.isSelected ? '#556ee6' : '#e9ecef',
    },
  }),
};

// Session management
const getUserSession = () => {
  const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  return {
    userId: authUser.userId || 1,
    userName: authUser.userName || "System",
    companyId: authUser.companyId || user.user_Company_Id || null,
    branchId: authUser.branchId || user.user_Branch_Id || null,
  };
};

// Helper: format local date/time without timezone
const formatLocalDate = (date) => {
  if (!date) return null;
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const formatLocalDateTime = (date) => {
  if (!date) return null;
  const ymd = formatLocalDate(date);
  const hh = String(date.getHours()).padStart(2, '0');
  const mm = String(date.getMinutes()).padStart(2, '0');
  const ss = String(date.getSeconds()).padStart(2, '0');
  return `${ymd}T${hh}:${mm}:${ss}`;
};

const defaultForm = {
  booking_Id: null,
  customer_Id: null,
  employee_Id: null,
  room_Id: null,
  from_Time: new Date(),
  to_Time: new Date(),
  entry_Date: new Date(),
  booking_Date: new Date(),
  is_Cancelled: false,
  cancellation_Reason: "",
  notes: "",
  source_Id: null,
  sourceOther: "",
  modified_By: null,
  modified_On: null,
  sendNotification: false,
  selectedServices: [],
  selectedPackages: [],
  selectedPackageSeries: [],
  business_Id: null,
  branch_Id: null,
};

const AddBookingPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [form, setForm] = useState(defaultForm);
  const [customers, setCustomers] = useState([]);
  const [customerSearchOptions, setCustomerSearchOptions] = useState([]);
  const [customerSearchLoading, setCustomerSearchLoading] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [selectedCustomerOption, setSelectedCustomerOption] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [equipments, setEquipments] = useState([]);
  const [services, setServices] = useState([]);
  const [packages, setPackages] = useState([]);
  const [packageSeries, setPackageSeries] = useState([]);
  const [serviceResources, setServiceResources] = useState([]);
  const [loading, setLoading] = useState(false);

  // Membership-related state
  const [customerMembership, setCustomerMembership] = useState(null);
  const [allCustomerMemberships, setAllCustomerMemberships] = useState([]);
  const [hasMembership, setHasMembership] = useState(false);
  const [membershipServices, setMembershipServices] = useState([]);
  const [membershipPackages, setMembershipPackages] = useState([]);
  const [membershipPackageSeries, setMembershipPackageSeries] = useState([]);
  const [formKey, setFormKey] = useState(0);

  // Refs to track previous selections for detecting re-selection
  const prevSelectedPackagesRef = useRef([]);
  const prevSelectedServicesRef = useRef([]);

  // Billed services from invoices
  const [billedStandaloneServices, setBilledStandaloneServices] = useState([]);
  const [loadingBilledServices, setLoadingBilledServices] = useState(false);
  // Track if customer has billable items (memberships or billed services)
  const [customerHasBillableItems, setCustomerHasBillableItems] = useState(false);

  // Availability checking - COMMENTED OUT per user request
  // const [timeAvailabilityResults, setTimeAvailabilityResults] = useState({});
  // const [checkingTimeAvailability, setCheckingTimeAvailability] = useState(false);
  // const [availabilityResults, setAvailabilityResults] = useState({});
  // const [checkingAvailability, setCheckingAvailability] = useState(false);
  // const [availabilityRefreshKey, setAvailabilityRefreshKey] = useState(0);

  // Consent form state
  const [showConsentModal, setShowConsentModal] = useState(false);
  const [consentData, setConsentData] = useState({
    witnessName: '',
    witnessSignature: '',
    guestSignature: ''
  });
  const [consentLoading, setConsentLoading] = useState(false);
  const [loggedInEmployeeName, setLoggedInEmployeeName] = useState('');
  const [sources, setSources] = useState([]);
  const [sourcesLoading, setSourcesLoading] = useState(false);
  const [businesses, setBusinesses] = useState([]);
  const [branches, setBranches] = useState([]);
  const [isFromInvoice, setIsFromInvoice] = useState(false);
  const [invoiceBranchId, setInvoiceBranchId] = useState(null);
  const [invoiceBusinessId, setInvoiceBusinessId] = useState(null);

  // Quick Add Customer state
  const [showQuickAddCustomer, setShowQuickAddCustomer] = useState(false);
  const [showCustomerProfile, setShowCustomerProfile] = useState(false);
  const [showAdvancedSearchModal, setShowAdvancedSearchModal] = useState(false);

  // Membership modal state
  const [showMembershipModal, setShowMembershipModal] = useState(false);
  const [selectedMembershipServices, setSelectedMembershipServices] = useState([]);
  const [selectedMembershipPackages, setSelectedMembershipPackages] = useState([]);
  const [selectedMembershipPackageSeries, setSelectedMembershipPackageSeries] = useState([]);

  // Table-based selection for membership services
  const [tableSelection, setTableSelection] = useState({}); // { selectionKey: { selected: boolean, requiredQty: number, membershipIds: [] } }

  // Calendar context (from clicking empty time slot)
  const [calendarContext, setCalendarContext] = useState({
    startTime: null,
    employeeId: null,
    employeeName: null,
    isFirstService: true // Track if this is the first service being added
  });

  const [isFromSpaCalendar, setIsFromSpaCalendar] = useState(false);

  // Check permissions
  const permissions = JSON.parse(localStorage.getItem('permissions') || '[]');
  const hasPermission = hasPagePermissionWithAdmin(permissions, '/GmsAddBooking');
  const isViewOnly = hasViewOnlyPermission(permissions, '/GmsAddBooking');
  const hasEditOrHigher = hasEditOrHigherPermission(permissions, '/GmsAddBooking');
  const isAdmin = isUserAdmin();

  // Get user session
  const session = useMemo(() => getUserSession(), []);

  // Source dropdown options
  const sourceOptions = sources.map(source => ({
    value: source.Source_Id || source.source_Id,
    label: source.SourceName || source.sourceName || 'N/A'
  }));

  // Business and Branch options
  const businessOptions = useMemo(() => {
    return businesses.map(b => ({
      value: b.business_Id,
      label: b.business_NameEn || b.business_Name
    }));
  }, [businesses]);

  const branchOptions = useMemo(() => {
    // Filter branches based on selected business
    const filteredBranches = form.business_Id
      ? branches.filter(branch => branch.branch_Buisness_Id === form.business_Id)
      : branches;

    return filteredBranches.map(b => ({
      value: b.branch_Id,
      label: b.branch_NameEn || b.branch_Name
    }));
  }, [branches, form.business_Id]);

  // Check if business/branch should be readonly
  const isBusinessReadonly = !!session.companyId || isFromInvoice;
  const isBranchReadonly = !!session.branchId || isFromInvoice;

  // Auto-populate company if user has companyId (only if not coming from invoice)
  useEffect(() => {
    if (!isFromInvoice && session.companyId && businesses.length > 0) {
      const companyId = typeof session.companyId === 'string' ? parseInt(session.companyId) : session.companyId;
      const companyExists = businesses.find(b => b.business_Id === companyId);
      if (companyExists && !form.business_Id) {
        setForm(prev => ({
          ...prev,
          business_Id: companyId
        }));
        // Load branches for this company
        loadBranches(companyId);
      }
    }
  }, [businesses, session.companyId, isFromInvoice]);

  // Auto-populate branch if user has branchId (only if not coming from invoice)
  useEffect(() => {
    if (!isFromInvoice && session.branchId && branches.length > 0 && form.business_Id) {
      const branchId = typeof session.branchId === 'string' ? parseInt(session.branchId) : session.branchId;
      const branchExists = branches.find(b => b.branch_Id === branchId && b.branch_Buisness_Id === form.business_Id);
      if (branchExists && !form.branch_Id) {
        setForm(prev => ({
          ...prev,
          branch_Id: branchId
        }));
      }
    }
  }, [branches, form.business_Id, session.branchId, isFromInvoice]);

  // Load branches when business changes
  useEffect(() => {
    if (form.business_Id) {
      loadBranches(form.business_Id);
      // Reset branch when business changes
      if (form.branch_Id) {
        const branchExists = branches.find(b => b.branch_Id === form.branch_Id && b.branch_Buisness_Id === form.business_Id);
        if (!branchExists) {
          setForm(prev => ({
            ...prev,
            branch_Id: null
          }));
        }
      }
    }
  }, [form.business_Id]);

  // Initialize refs with current form state
  useEffect(() => {
    if (prevSelectedPackagesRef.current.length === 0 && form.selectedPackages?.length > 0) {
      prevSelectedPackagesRef.current = form.selectedPackages;
    }
  }, []);

  useEffect(() => {
    if (prevSelectedServicesRef.current.length === 0 && form.selectedServices?.length > 0) {
      prevSelectedServicesRef.current = form.selectedServices;
    }
  }, []);

  useEffect(() => {
    if (hasPermission) {
      loadInitialData();
      loadLoggedInEmployeeName();

      // Check if there's a date parameter in the URL (from calendar page)
      const urlParams = new URLSearchParams(location.search);
      const dateParam = urlParams.get('date');

      if (dateParam) {
        try {
          const selectedDate = new Date(dateParam);
          if (!isNaN(selectedDate.getTime())) {
            setForm(prev => ({
              ...prev,
              booking_Date: selectedDate
            }));
          }
        } catch (error) {
          console.error('Error parsing date from URL:', error);
        }
      }

      // Check if opened from Spa Calendar
      if (location.state?.fromSpaCalendar) {
        setIsFromSpaCalendar(true);
      }

      // Handle calendar context (from clicking empty time slot)
      if (location.state?.fromCalendar) {
        const { bookingDate, startTime, employeeId, employeeName } = location.state;

        // Set booking date if provided
        if (bookingDate) {
          try {
            const selectedDate = new Date(bookingDate);
            if (!isNaN(selectedDate.getTime())) {
              setForm(prev => ({
                ...prev,
                booking_Date: selectedDate
              }));
            }
          } catch (error) {
            console.error('Error parsing booking date from calendar:', error);
          }
        }

        // Store calendar context
        if (startTime) {
          try {
            const parsedStartTime = new Date(startTime);
            if (!isNaN(parsedStartTime.getTime())) {
              setCalendarContext({
                startTime: parsedStartTime,
                employeeId: employeeId || null,
                employeeName: employeeName || null,
                isFirstService: true
              });
              console.log('Calendar context set:', {
                startTime: parsedStartTime,
                employeeId,
                employeeName
              });
            }
          } catch (error) {
            console.error('Error parsing start time from calendar:', error);
          }
        }

        // Clear navigation state
        window.history.replaceState({}, document.title);
      }

      // Handle navigation from billing page
      if (location.state?.fromInvoice) {
        setIsFromInvoice(true);
        const { customerId, businessId, branchId, sourceId, invoiceDetails } = location.state;
        console.log('--- Navigated from Invoice ---', { customerId, businessId, branchId, sourceId });
        // Store businessId and branchId for later use (since we clear location.state)
        if (businessId) {
          const businessIdNum = typeof businessId === 'string' ? parseInt(businessId) : businessId;
          setInvoiceBusinessId(businessIdNum);
        }
        if (branchId) {
          const branchIdNum = typeof branchId === 'string' ? parseInt(branchId) : branchId;
          setInvoiceBranchId(branchIdNum);
        }

        if (sourceId) {
          const sourceIdNum = typeof sourceId === 'string' ? parseInt(sourceId) : sourceId;
          setForm(prev => ({ ...prev, source_Id: sourceIdNum }));
        }

        if (customerId) {
          // Fetch customer data and set customer option
          const setCustomerFromId = async () => {
            try {
              const customerData = await getCustomers({ customer_Id: customerId });
              if (customerData && customerData.length > 0) {
                const customer = customerData[0];
                setSelectedCustomerOption({
                  value: customerId,
                  label: `${customer.customer_NameEn || 'N/A'} — ${customer.customer_ContactNo || 'N/A'}`
                });
              } else {
                setSelectedCustomerOption({
                  value: customerId,
                  label: `Customer ID: ${customerId}`
                });
              }
            } catch (error) {
              console.error('Error fetching customer:', error);
              setSelectedCustomerOption({
                value: customerId,
                label: `Customer ID: ${customerId}`
              });
            }
            // Set customer in form
            handleFormChange('customer_Id', customerId);
            // Auto-open modal after customer is loaded
            setTimeout(() => {
              setShowMembershipModal(true);
            }, 1500);
          };
          setCustomerFromId();
        }
        // Clear navigation state
        window.history.replaceState({}, document.title);
      }
    } else {
      window.location.href = '/Dashboard';
    }
  }, [hasPermission, location.search, location.state]);

  // Set business ID from invoice when businesses are loaded
  useEffect(() => {
    if (isFromInvoice && invoiceBusinessId && businesses.length > 0 && !form.business_Id) {
      const companyExists = businesses.find(b => b.business_Id === invoiceBusinessId);
      if (companyExists) {
        setForm(prev => ({
          ...prev,
          business_Id: invoiceBusinessId
        }));
        // Load branches for this company
        loadBranches(invoiceBusinessId);
      }
    }
  }, [businesses, isFromInvoice, invoiceBusinessId, form.business_Id]);

  // Set branch ID from invoice when branches are loaded
  useEffect(() => {
    if (isFromInvoice && invoiceBranchId && branches.length > 0 && form.business_Id) {
      const branchExists = branches.find(b => b.branch_Id === invoiceBranchId && b.branch_Buisness_Id === form.business_Id);
      if (branchExists && !form.branch_Id) {
        setForm(prev => ({
          ...prev,
          branch_Id: invoiceBranchId
        }));
      }
    }
  }, [branches, form.business_Id, isFromInvoice, invoiceBranchId]);

  // Check availability when service resources or booking date changes - COMMENTED OUT per user request
  // useEffect(() => {
  //   if (serviceResources.length > 0 && form.booking_Date) {
  //     // Use setTimeout to ensure state is fully updated
  //     const timeoutId = setTimeout(async () => {
  //       // Check time-based availability for each resource that has time set
  //       const resourcesToCheck = serviceResources.filter(resource => resource.fromTime && resource.toTime);
  //       
  //       if (resourcesToCheck.length > 0) {
  //         // Check time-based availability for all resources in parallel
  //         // Pass false to prevent individual refresh key increments
  //         // We'll increment once after all checks complete
  //         const availabilityPromises = resourcesToCheck.map(resource => 
  //           checkTimeAvailability(resource.id, resource.fromTime, resource.toTime, false)
  //         );
  //         
  //         await Promise.all(availabilityPromises);
  //         
  //         // Increment refresh key once after all availability checks complete
  //         // This forces Select components to re-render with updated availability data
  //         setAvailabilityRefreshKey(prev => prev + 1);
  //       }
  //       
  //       // Always trigger bulk availability check to refresh employee and room dropdowns
  //       // This is especially important when booking date changes
  //       checkAvailabilityForSelection();
  //     }, 100);
  //     
  //     return () => clearTimeout(timeoutId);
  //   }
  // }, [serviceResources, form.booking_Date]);

  const loadInitialData = async () => {
    try {
      await Promise.all([
        loadCustomers(),
        loadEmployees(),
        loadRooms(),
        loadEquipments(),
        loadServices(),
        loadPackages(),
        loadPackageSeries(),
        loadSources(),
        loadBusinesses(),
        loadBranches(),
      ]);
    } catch (error) {
      console.error("Error loading initial data:", error);
      Swal.fire("Error", "Failed to load initial data", "error");
    }
  };

  const loadSources = async () => {
    setSourcesLoading(true);
    try {
      const filter = {
        Page: 1,
        PageSize: 1000,
      };
      const response = await getSources(filter);
      const sourcesList = response?.Sources || response?.sources || [];

      if (Array.isArray(sourcesList) && sourcesList.length > 0) {
        setSources(sourcesList);
      } else if (Array.isArray(response)) {
        setSources(response);
      } else {
        setSources([]);
      }
    } catch (error) {
      console.error("Error loading sources:", error);
      setSources([]);
    } finally {
      setSourcesLoading(false);
    }
  };

  const loadBusinesses = async () => {
    try {
      const response = await getBusinesses({ business_Active: true });
      const data = response.data || response;
      if (Array.isArray(data)) {
        setBusinesses(data);
      } else {
        setBusinesses([]);
      }
    } catch (error) {
      console.error("Error loading businesses:", error);
      setBusinesses([]);
    }
  };

  const loadBranches = async (businessId = null) => {
    try {
      const filter = businessId ? { Business_Id: businessId, branch_Active: true } : { branch_Active: true };
      const response = await getBranches(filter);
      const data = response.data || response;
      if (Array.isArray(data)) {
        setBranches(data);
      } else {
        setBranches([]);
      }
    } catch (error) {
      console.error("Error loading branches:", error);
      setBranches([]);
    }
  };

  const loadCustomers = async () => {
    try {
      const data = await getCustomers({});
      const formattedCustomers = data.map(customer => ({
        value: customer.customer_Id,
        label: customer.customer_NameEn || customer.customer_Name,
      }));
      setCustomers(formattedCustomers);
    } catch (error) {
      console.error('Error loading customers:', error);
      Swal.fire("Error", "Failed to load customers", "error");
    }
  };

  const searchCustomersManually = async (inputValue) => {
    // Trim spaces from the search text to handle cases like "052 422 5719"
    const cleanedInput = inputValue ? inputValue.replace(/\s+/g, '') : '';
    const originalInput = inputValue ? inputValue.trim() : '';

    if (!originalInput || originalInput.length < 3) {
      setCustomerSearchOptions([]);
      return;
    }

    try {
      setCustomerSearchLoading(true);
      // Try searching with the cleaned input (no spaces)
      const response = await searchCustomers(cleanedInput, 1, 20);

      let formattedOptions = response.items.map(customer => ({
        value: customer.customer_Id,
        label: `${customer.customer_NameEn || 'N/A'} — ${customer.customer_ContactNo || 'N/A'}`,
        customer: customer
      }));

      // If no results with cleaned input, try with original input (optional, but safer)
      if (formattedOptions.length === 0 && cleanedInput !== originalInput) {
        const responseOriginal = await searchCustomers(originalInput, 1, 20);
        formattedOptions = responseOriginal.items.map(customer => ({
          value: customer.customer_Id,
          label: `${customer.customer_NameEn || 'N/A'} — ${customer.customer_ContactNo || 'N/A'}`,
          customer: customer
        }));
      }

      setCustomerSearchOptions(formattedOptions);
    } catch (error) {
      console.error('Error searching customers:', error);
      setCustomerSearchOptions([]);
    } finally {
      setCustomerSearchLoading(false);
    }
  };

  const handleCustomerAdded = async (newCustomer) => {
    const customerId = newCustomer.customer_Id || newCustomer.id;

    try {
      const customerData = await getCustomers({ customer_Id: customerId });
      if (customerData && customerData.length > 0) {
        const addedCustomer = customerData[0];
        setForm(prev => ({
          ...prev,
          customer_Id: customerId
        }));
        setSelectedCustomerOption({
          value: customerId,
          label: `${addedCustomer.customer_NameEn || 'N/A'} — ${addedCustomer.customer_ContactNo || 'N/A'}`
        });
        loadCustomers();
      } else {
        setForm(prev => ({
          ...prev,
          customer_Id: customerId
        }));
        setSelectedCustomerOption({
          value: customerId,
          label: `Customer ID: ${customerId}`
        });
        loadCustomers();
      }
    } catch (error) {
      console.error("Error fetching customer:", error);
      setForm(prev => ({
        ...prev,
        customer_Id: customerId
      }));
      setSelectedCustomerOption({
        value: customerId,
        label: `Customer ID: ${customerId}`
      });
    }
  };

  const loadEmployees = async () => {
    try {
      // Build filter based on logged-in user's company and/or branch
      const filter = {};

      // Filter by company (business id) if available
      if (session.companyId) {
        filter.Employee_Business_Id = session.companyId;
      }

      // Filter by branch if available (only if company is also available)
      if (session.branchId && session.companyId) {
        filter.Employee_Branch_Id = session.branchId;
      }

      const data = await getEmployees(filter);
      const formattedEmployees = data.map(emp => ({
        value: emp.employee_Id,
        label: emp.employee_NameEn || emp.employee_Name,
      }));
      setEmployees(formattedEmployees);
    } catch (error) {
      console.error('Error loading employees:', error);
      Swal.fire("Error", "Failed to load employees", "error");
    }
  };

  const loadRooms = async () => {
    try {
      const data = await getRooms({});
      const formattedRooms = data.map(room => ({
        value: room.room_Id,
        label: room.room_Name_English || room.room_Name_Arabic || room.room_Name,
      }));
      setRooms(formattedRooms);
    } catch (error) {
      console.error('Error loading rooms:', error);
      Swal.fire("Error", "Failed to load rooms", "error");
    }
  };

  const loadServices = async () => {
    try {
      const data = await getSpaServices({ status: true });
      // Additional client-side filtering to ensure only active services are stored
      const activeServices = (data || []).filter(service => service.status === true);
      const formattedServices = activeServices.map(service => ({
        value: service.spaService_Id,
        label: service.spaServiceName_En || service.spaServiceName_Ar || service.spaService_NameEn || service.spaService_Name,
        duration: service.duration || 30,
      }));
      setServices(formattedServices);
    } catch (error) {
      console.error('Error loading services:', error);
      Swal.fire("Error", "Failed to load services", "error");
    }
  };

  const loadPackages = async () => {
    try {
      const data = await getSpaPackages({ status: true });
      // Additional client-side filtering to ensure only active packages are stored
      const activePackages = (data || []).filter(pkg => pkg.status === true);
      const formattedPackages = activePackages.map(pkg => ({
        value: pkg.packageId,
        label: pkg.packageName_En || pkg.packageName_Ar || pkg.package_NameEn || pkg.package_Name,
      }));
      setPackages(formattedPackages);
    } catch (error) {
      console.error('Error loading packages:', error);
      Swal.fire("Error", "Failed to load packages", "error");
    }
  };

  const loadPackageSeries = async () => {
    try {
      const data = await getSpaPackageSeries({ status: true });
      // Additional client-side filtering to ensure only active package series are stored
      const activePackageSeries = (data || []).filter(series => series.status === true);
      const formattedPackageSeries = activePackageSeries.map(series => ({
        value: series.seriesId,
        label: series.seriesName_En || series.seriesName_Ar,
      }));
      setPackageSeries(formattedPackageSeries);
    } catch (error) {
      console.error('Error loading package series:', error);
      Swal.fire("Error", "Failed to load package series", "error");
    }
  };

  const loadLoggedInEmployeeName = async () => {
    try {
      const authUser = JSON.parse(localStorage.getItem('authUser') || '{}');
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const employeeId = authUser.employeeId || user.user_EmployeeId;

      if (employeeId) {
        const employees = await getEmployees({ employee_Id: employeeId });
        if (employees && employees.length > 0) {
          const employee = employees[0];
          const employeeName = employee.employee_NameEn || employee.employee_Name || '';
          setLoggedInEmployeeName(employeeName);
          setConsentData(prev => ({
            ...prev,
            witnessName: prev.witnessName || employeeName
          }));
        }
      }
    } catch (error) {
      console.error('Error loading logged-in employee name:', error);
    }
  };

  const loadEquipments = async () => {
    try {
      const data = await getEquipments({ status: true });
      const formattedEquipments = data.map(equipment => ({
        value: equipment.eq_Id,
        label: equipment.eq_Name_English || equipment.eq_Name_Arabic,
      }));
      setEquipments(formattedEquipments);
    } catch (error) {
      console.error('Error loading equipments:', error);
      Swal.fire("Error", "Failed to load equipments", "error");
    }
  };

  const fetchPackageServices = async (packageIds) => {
    try {
      const allPackageServices = [];
      const seenServiceKeys = new Set(); // Track services to prevent duplicates within the same package

      for (const packageId of packageIds) {
        const packageData = await getSpaPackageById(packageId);
        // Only process active packages
        if (packageData && packageData.status === true && packageData.details) {
          packageData.details.forEach(detail => {
            const service = services.find(s => s.value === detail.service_Id);
            // Only include services that are in the active services list
            if (service) {
              // Create a key to detect duplicates (same package + same service)
              // Note: We allow the same service to appear multiple times if it has different quantities
              // But we prevent exact duplicates (same package + same service + same quantity)
              const serviceKey = `${packageId}_${detail.service_Id}_${detail.quantity || 1}`;

              if (seenServiceKeys.has(serviceKey)) {
                console.warn(`⚠️ Duplicate service in package ${packageId}: Service ${detail.service_Id} with quantity ${detail.quantity || 1} - skipping duplicate`);
                return; // Skip this duplicate
              }
              seenServiceKeys.add(serviceKey);

              allPackageServices.push({
                ...service,
                packageId: packageId,
                packageName: packageData.packageName_En || packageData.packageName_Ar,
                serviceId: detail.service_Id,
                quantity: detail.quantity || 1,
                duration: service.duration || 30
              });
            }
          });
        }
      }

      return allPackageServices;
    } catch (error) {
      console.error('Error fetching package services:', error);
      return [];
    }
  };

  const loadBilledStandaloneServices = async (customerId) => {
    if (!customerId) {
      setBilledStandaloneServices([]);
      return;
    }

    try {
      setLoadingBilledServices(true);
      const billedServices = await getCustomerBilledStandaloneServices({
        CustomerId: customerId,
        BranchId: null // You can add branch filter if needed
      });
      setBilledStandaloneServices(billedServices || []);
    } catch (error) {
      console.error('Error loading billed standalone services:', error);
      setBilledStandaloneServices([]);
    } finally {
      setLoadingBilledServices(false);
    }
  };

  const checkCustomerMembership = async (customerId) => {
    if (!customerId) {
      setCustomerMembership(null);
      setAllCustomerMemberships([]);
      setHasMembership(false);
      setMembershipServices([]);
      setMembershipPackages([]);
      setMembershipPackageSeries([]);
      setBilledStandaloneServices([]);
      setCustomerHasBillableItems(false);
      return;
    }

    try {
      // Load both memberships and billed services in parallel
      const [memberships, billedServices] = await Promise.all([
        getCustomerMemberships({ customerId: customerId }),
        getCustomerBilledStandaloneServices({
          CustomerId: customerId,
          BranchId: null
        }).catch(err => {
          console.error('Error fetching billed standalone services:', err);
          return []; // Return empty array on error
        })
      ]);

      // Set billed services
      const hasBilledServices = billedServices && billedServices.length > 0;
      setBilledStandaloneServices(billedServices || []);

      // Check if customer has memberships
      const hasMemberships = memberships && memberships.length > 0;

      // Set billable items status (memberships OR billed services)
      setCustomerHasBillableItems(hasMemberships || hasBilledServices);

      if ((hasMemberships || hasBilledServices) && !isFromInvoice) {
        Swal.fire({
          title: 'Billed Services Found',
          text: 'This customer has billed services or memberships. Would you like to view and select them now?',
          icon: 'question',
          showCancelButton: true,
          confirmButtonText: 'Yes',
          cancelButtonText: 'No',
          confirmButtonColor: '#3085d6',
          cancelButtonColor: '#d33',
        }).then((result) => {
          if (result.isConfirmed) {
            openMembershipModal(true);
          }
        });
      }

      if (hasMemberships) {
        setAllCustomerMemberships(memberships);
        const membership = memberships[0];
        setCustomerMembership(membership);
        setHasMembership(true);

        let allPendingDetails = [];
        let allSeriesIds = new Set();

        memberships.forEach(m => {
          if (m.details && m.details.length > 0) {
            const pendingDetails = m.details.filter(detail => detail.completionStatus === "Pending");
            allPendingDetails = [...allPendingDetails, ...pendingDetails];
          }
          if (m.installments && m.installments.length > 0) {
            m.installments.forEach(inst => {
              if (inst.seriesId) {
                allSeriesIds.add(inst.seriesId);
              }
            });
          }
        });

        const uniqueServiceIds = [...new Set(allPendingDetails.map(detail => detail.serviceId).filter(Boolean))];
        const uniquePackageIds = [...new Set(allPendingDetails.map(detail => detail.packageId).filter(Boolean))];
        const uniqueSeriesIds = Array.from(allSeriesIds);

        const membershipServicesData = [];
        const membershipPackagesData = [];
        const membershipPackageSeriesData = [];

        uniqueServiceIds.forEach(serviceId => {
          const service = services.find(s => s.value === serviceId);
          if (service) {
            membershipServicesData.push(service);
          }
        });

        uniquePackageIds.forEach(packageId => {
          const pkg = packages.find(p => p.value === packageId);
          if (pkg) {
            membershipPackagesData.push(pkg);
          }
        });

        uniqueSeriesIds.forEach(seriesId => {
          const series = packageSeries.find(s => s.value === seriesId);
          if (series) {
            membershipPackageSeriesData.push(series);
          }
        });

        setMembershipServices(membershipServicesData);
        setMembershipPackages(membershipPackagesData);
        setMembershipPackageSeries(membershipPackageSeriesData);
      } else {
        setCustomerMembership(null);
        setAllCustomerMemberships([]);
        setHasMembership(false);
        setMembershipServices([]);
        setMembershipPackages([]);
        setMembershipPackageSeries([]);
      }
    } catch (error) {
      console.error('Error checking customer membership:', error);
      setCustomerMembership(null);
      setAllCustomerMemberships([]);
      setHasMembership(false);
      setMembershipServices([]);
      setMembershipPackages([]);
      setMembershipPackageSeries([]);
      setCustomerHasBillableItems(false);
    }
  };

  // COMMENTED OUT per user request - availability checks not needed
  // const checkTimeAvailability = async (resourceId, fromTime, toTime, shouldIncrementRefreshKey = true) => {
  //   if (!form.booking_Date || !fromTime || !toTime) {
  //     return;
  //   }

  //   try {
  //     setCheckingTimeAvailability(true);
  //     
  //     const bookingDate = new Date(form.booking_Date);
  //     const bookingDateStr = formatLocalDate(bookingDate);
  //     const fromTimeStr = formatLocalDateTime(fromTime);
  //     const toTimeStr = formatLocalDateTime(toTime);

  //     const timeData = {
  //       booking_Id: null,
  //       booking_Date: bookingDateStr,
  //       from_Time: fromTimeStr,
  //       to_Time: toTimeStr
  //     };

  //     const results = await checkAvailabilityForTime(timeData);
  //     
  //     setTimeAvailabilityResults(prev => ({
  //       ...prev,
  //       [resourceId]: results
  //     }));
  //     
  //     // Increment refresh key to force Select components to refresh
  //     // Only increment if explicitly requested (for manual time changes)
  //     // When called from useEffect, we'll increment once after all checks complete
  //     if (shouldIncrementRefreshKey) {
  //       setAvailabilityRefreshKey(prev => prev + 1);
  //     }
  //   } catch (error) {
  //     console.error('Error checking time availability:', error);
  //   } finally {
  //     setCheckingTimeAvailability(false);
  //   }
  // };

  // COMMENTED OUT per user request - availability checks not needed
  // Check availability for service resources
  // const checkAvailabilityForSelection = async () => {
  //   if (!form.booking_Date || serviceResources.length === 0) {
  //     return;
  //   }

  //   try {
  //     setCheckingAvailability(true);
  //     
  //     const bookingDate = new Date(form.booking_Date);
  //     const bookingDateStr = formatLocalDate(bookingDate);

  //     // Create service resources for availability check
  //     const serviceResourcesForCheck = serviceResources
  //       .filter(resource => resource.employeeId || resource.roomId) // Only include resources with employee or room
  //       .map(resource => ({
  //         employee_Id: resource.employeeId || null,
  //         room_Id: resource.roomId || null,
  //         from_Time: resource.fromTime ? formatLocalDateTime(resource.fromTime) : null,
  //         to_Time: resource.toTime ? formatLocalDateTime(resource.toTime) : null
  //       }));

  //     // Don't make API call if no valid resources
  //     if (serviceResourcesForCheck.length === 0) {
  //       setAvailabilityResults({});
  //       return;
  //     }

  //     const availabilityData = {
  //       booking_Id: null, // Always null for new bookings
  //       booking_Date: bookingDateStr,
  //       serviceResources: serviceResourcesForCheck
  //     };

  //     console.log('Availability check data:', availabilityData);
  //     const results = await checkAvailability(availabilityData);
  //     console.log('Availability results:', results);
  //     
  //     // Convert results to a map for easy lookup
  //     const availabilityMap = {};
  //     results.forEach(result => {
  //       availabilityMap[`${result.type}_${result.id}`] = result;
  //     });
  //     
  //     setAvailabilityResults(availabilityMap);
  //   } catch (error) {
  //     console.error('Error checking availability:', error);
  //     // Don't show error to user as this is a background check
  //   } finally {
  //     setCheckingAvailability(false);
  //   }
  // };

  // COMMENTED OUT per user request - availability checks not needed
  // Now returns plain options without availability indicators
  const formatOptionsWithAvailability = (options, type, resourceId) => {
    // Return plain options without availability checks
    return options;

    // OLD CODE - COMMENTED OUT
    // const timeAvailability = timeAvailabilityResults[resourceId];
    // 
    // return options.map(option => {
    //   let isAvailable = true;
    //   
    //   if (timeAvailability) {
    //     if (type === 'Employee') {
    //       const employee = timeAvailability.availableEmployees?.find(emp => emp.employee_Id === option.value);
    //       isAvailable = employee ? employee.isAvailable : true;
    //     } else if (type === 'Room') {
    //       const room = timeAvailability.availableRooms?.find(room => room.room_Id === option.value);
    //       isAvailable = room ? room.isAvailable : true;
    //     }
    //   }
    //   
    //   return {
    //     ...option,
    //     isDisabled: !isAvailable,
    //     label: (
    //       <div className="d-flex align-items-center">
    //         <span className={`me-2 ${isAvailable ? 'text-success' : 'text-danger'}`}>
    //           {isAvailable ? '✓' : '✗'}
    //         </span>
    //         <span>{option.label}</span>
    //       </div>
    //     )
    //   };
    // });
  };

  const handleFormChange = async (field, value, actionMeta = null) => {
    // react-select actionMeta is critical here:
    // - action === 'deselect-option' happens when user clicks an already-selected option in the MENU.
    //   We interpret that as "add another instance" and we keep the option selected (no toggle-off UX).
    // - action === 'remove-value' / 'pop-value' / 'clear' are real removals (we do NOT auto-add instances).
    const action = actionMeta?.action;
    const clickedOption = actionMeta?.option;

    let effectiveValue = value;
    const isMultiEntityField = field === 'selectedPackages' || field === 'selectedServices';
    if (isMultiEntityField) {
      effectiveValue = value || [];
      if (action === 'deselect-option' && clickedOption) {
        const exists = effectiveValue.some(o => o?.value === clickedOption.value);
        effectiveValue = exists ? effectiveValue : [...effectiveValue, clickedOption];
      }
    }

    setForm(prev => ({
      ...prev,
      [field]: isMultiEntityField ? effectiveValue : value
    }));

    if (field === 'customer_Id') {
      if (value) {
        checkCustomerMembership(value); // This now loads both memberships and billed services
      } else {
        setServiceResources([]);
        setForm(prev => ({
          ...prev,
          selectedServices: [],
          selectedPackages: [],
          selectedPackageSeries: []
        }));
        setCustomerMembership(null);
        setAllCustomerMemberships([]);
        setHasMembership(false);
        setMembershipServices([]);
        setMembershipPackages([]);
        setMembershipPackageSeries([]);
        setBilledStandaloneServices([]);
        setCustomerHasBillableItems(false);
        setConsentData({
          witnessName: loggedInEmployeeName,
          witnessSignature: '',
          guestSignature: ''
        });
      }
    }

    if (field === 'selectedPackages') {
      const prevSelected = prevSelectedPackagesRef.current;
      const prevPackageIds = prevSelected.map(pkg => pkg.value);

      // Detect newly added packages (in current but not in previous)
      const newlyAddedPackages = (effectiveValue || []).filter(pkg => !prevPackageIds.includes(pkg.value));

      // Update ref to track current selection
      prevSelectedPackagesRef.current = effectiveValue || [];

      // Get current serviceResources to check what exists
      const currentResources = serviceResources.filter(r => r.packageId && !r.membershipId);
      const existingPackageIdsInTable = new Set(currentResources.map(r => r.packageId));

      if (effectiveValue && effectiveValue.length > 0) {
        const packageIds = effectiveValue.map(pkg => pkg.value);
        const packageServices = await fetchPackageServices(packageIds);

        setServiceResources(prev => {
          // NEVER remove package resources when deselected from dropdown
          // Only remove them via the Remove button in Service Resource Allocation table
          // Keep all existing package resources regardless of selection state
          const filtered = prev.filter(resource => {
            if (resource.packageId && !resource.membershipId) {
              // Always keep package resources - don't remove based on dropdown selection
              return true;
            }
            return true;
          });

          // Count existing instances for each package
          // Also initialize packageInstanceId for existing resources that don't have it
          const existingInstanceCounts = {};
          const updatedFiltered = filtered.map(resource => {
            if (resource.packageId && !resource.membershipId) {
              // Initialize packageInstanceId if missing (for backward compatibility)
              if (resource.packageInstanceId === undefined) {
                resource.packageInstanceId = 1;
              }
              const instanceId = resource.packageInstanceId;
              existingInstanceCounts[resource.packageId] = Math.max(
                existingInstanceCounts[resource.packageId] || 0,
                instanceId
              );
            }
            return resource;
          });

          // Decide which package(s) to add allocations for:
          // - Normal selection: newly added packages only
          // - Menu click on already-selected option: actionMeta.action === 'deselect-option'
          //   -> add another instance for the clicked option
          const addAnotherPackage = (action === 'deselect-option' && clickedOption) ? clickedOption : null;
          const packagesToProcess = addAnotherPackage ? [addAnotherPackage] : newlyAddedPackages;

          const newPackageIds = packagesToProcess.map(pkg => pkg.value);
          const newPackageServices = packageServices.filter(service => newPackageIds.includes(service.packageId));

          // If no packages to process, return existing resources
          if (newPackageServices.length === 0) {
            return updatedFiltered;
          }

          // Group services by package to assign instance IDs
          const servicesByPackage = {};
          newPackageServices.forEach(service => {
            if (!servicesByPackage[service.packageId]) {
              servicesByPackage[service.packageId] = [];
            }
            servicesByPackage[service.packageId].push(service);
          });

          // Expand services based on quantity - create multiple entries for services with quantity > 1
          const expandedServices = [];
          Object.keys(servicesByPackage).forEach(packageId => {
            const packageInstanceId = (existingInstanceCounts[packageId] || 0) + 1;
            servicesByPackage[packageId].forEach(service => {
              const quantity = service.quantity || 1;
              for (let q = 0; q < quantity; q++) {
                expandedServices.push({
                  ...service,
                  quantityIndex: q, // Track which instance this is (0, 1, 2, etc.)
                  packageInstanceId: packageInstanceId // Track which package instance this belongs to
                });
              }
            });
          });

          // Calculate base time - use calendar start time if available and this is first service
          const bookingDate = new Date(form.booking_Date);
          let baseTime = new Date(bookingDate);
          baseTime.setHours(9, 0, 0, 0);

          let useCalendarStartTime = false;
          if (calendarContext.isFirstService && calendarContext.startTime) {
            const calendarStart = new Date(calendarContext.startTime);
            if (calendarStart.getFullYear() === bookingDate.getFullYear() &&
              calendarStart.getMonth() === bookingDate.getMonth() &&
              calendarStart.getDate() === bookingDate.getDate()) {
              baseTime = new Date(calendarStart);
              useCalendarStartTime = true;
              console.log('Using calendar start time for package:', baseTime);
            }
          }

          // If not using calendar start time, and there are already resources, start after the last one to ensure sequential timing
          if (!useCalendarStartTime && prev.length > 0) {
            // Find the latest end time among existing resources
            let latestEndTime = baseTime;
            prev.forEach(resource => {
              if (resource.toTime) {
                const resourceToTime = new Date(resource.toTime);
                if (resourceToTime > latestEndTime) {
                  latestEndTime = resourceToTime;
                }
              }
            });
            baseTime = latestEndTime;
          }

          const newResources = expandedServices.map((service, index) => {
            let cumulativeMinutes = 0;
            for (let i = 0; i < index; i++) {
              const prevService = expandedServices[i];
              const prevDuration = prevService.duration || 30;
              cumulativeMinutes += prevDuration;
            }

            const fromTime = new Date(baseTime.getTime() + (cumulativeMinutes * 60000));
            const duration = service.duration || 30;
            const toTime = new Date(fromTime.getTime() + (duration * 60000));

            // Update baseTime for next iteration
            baseTime = toTime;

            return {
              id: `pkg-${service.packageId}-${service.serviceId}-${Date.now()}-${Math.random()}-${index}-${service.quantityIndex}`,
              serviceId: service.serviceId,
              serviceName: service.label,
              packageId: service.packageId,
              packageName: service.packageName,
              packageInstanceId: service.packageInstanceId, // Track package instance
              employeeId: calendarContext.employeeId || null, // Pre-select employee from calendar
              roomId: null,
              equipmentId: null,
              fromTime: fromTime,
              toTime: toTime
            };
          });

          // Mark that first service has been added
          if (calendarContext.isFirstService) {
            setCalendarContext(prev => ({ ...prev, isFirstService: false }));
          }
          const updatedResources = [...updatedFiltered, ...newResources];

          // COMMENTED OUT per user request - availability checks not needed
          // Trigger availability checks after resources are added
          // setTimeout(() => {
          //   updatedResources.forEach(resource => {
          //     if (resource.fromTime && resource.toTime) {
          //       checkTimeAvailability(resource.id, resource.fromTime, resource.toTime);
          //     }
          //   });
          //   checkAvailabilityForSelection();
          // }, 500);

          return updatedResources;
        });
      } else {
        // Don't remove package resources when all packages are cleared from dropdown
        // They can only be removed via the Remove button in Service Resource Allocation table
        // setServiceResources(prev => prev.filter(resource => !(resource.packageId && !resource.membershipId)));
      }
    }

    if (field === 'selectedServices') {
      const prevSelected = prevSelectedServicesRef.current;
      const prevServiceIds = prevSelected.map(svc => svc.value);

      // Detect newly added services (in current but not in previous)
      const newlyAddedServices = (effectiveValue || []).filter(svc => !prevServiceIds.includes(svc.value));

      // Update ref to track current selection
      prevSelectedServicesRef.current = effectiveValue || [];

      if (effectiveValue && effectiveValue.length > 0) {
        setServiceResources(prev => {
          // Separate resources into different categories:
          // 1. Standalone billed services (have invoiceDetailId) - preserve these
          // 2. Regular standalone services (no packageId, no membershipId, no invoiceDetailId)
          // 3. Package/membership services (have packageId or membershipId)

          const standaloneBilledResources = prev.filter(resource => resource.invoiceDetailId);
          const existingIndividualResources = prev.filter(resource =>
            !resource.packageId && !resource.membershipId && !resource.invoiceDetailId
          );
          const packageMembershipResources = prev.filter(resource =>
            resource.packageId || resource.membershipId
          );

          // Check what services exist in the original resources (before filtering)
          const existingServiceIdsInTable = new Set(existingIndividualResources.map(r => r.serviceId));

          const selectedServiceIds = effectiveValue.map(service => service.value);

          // NEVER remove service resources when deselected from dropdown
          // Only remove them via the Remove button in Service Resource Allocation table
          // Keep all existing individual service resources regardless of selection state
          const preservedResources = existingIndividualResources; // Keep all, don't filter

          // Process services: newly added OR services that exist in original table (re-selected)
          // If a service exists in table and is being selected, add a new instance
          const addAnotherService = (action === 'deselect-option' && clickedOption) ? clickedOption : null;
          const servicesToProcess = addAnotherService ? [addAnotherService] : newlyAddedServices;

          // If no services to process, return existing resources
          if (servicesToProcess.length === 0) {
            return [
              ...packageMembershipResources,
              ...standaloneBilledResources,
              ...preservedResources
            ];
          }

          const newServices = servicesToProcess;

          // Calculate time based on existing resources to ensure sequential timing
          const bookingDate = new Date(form.booking_Date);
          let latestEndTime = new Date(bookingDate);
          latestEndTime.setHours(9, 0, 0, 0);

          // If calendar context exists and this is the first service, use calendar start time
          let useCalendarStartTime = false;
          if (calendarContext.isFirstService && calendarContext.startTime) {
            const calendarStart = new Date(calendarContext.startTime);
            // Check if calendar start time is on the same date as booking date
            if (calendarStart.getFullYear() === bookingDate.getFullYear() &&
              calendarStart.getMonth() === bookingDate.getMonth() &&
              calendarStart.getDate() === bookingDate.getDate()) {
              latestEndTime = new Date(calendarStart);
              useCalendarStartTime = true;
              console.log('Using calendar start time:', latestEndTime);
            }
          }

          // Find the latest end time from all existing resources to ensure sequential timing
          if (!useCalendarStartTime && prev.length > 0) {
            let latestResEndTime = latestEndTime;
            prev.forEach(resource => {
              if (resource.toTime) {
                const resourceToTime = new Date(resource.toTime);
                if (resourceToTime > latestResEndTime) {
                  latestResEndTime = resourceToTime;
                }
              }
            });
            latestEndTime = latestResEndTime;
          }

          const newResources = newServices.map((service, index) => {
            // Calculate start time: use latest end time from existing resources
            let fromTime = new Date(latestEndTime);

            // If this is not the first new service, add duration of previous new services
            if (index > 0) {
              for (let i = 0; i < index; i++) {
                const prevService = newServices[i];
                const prevDuration = prevService.duration || 30;
                fromTime = new Date(fromTime.getTime() + (prevDuration * 60000));
              }
            }

            const duration = service.duration || 30;
            const toTime = new Date(fromTime.getTime() + (duration * 60000));

            // Update latestEndTime for next iteration
            latestEndTime = toTime;

            return {
              id: `srv-${service.value}-${Date.now()}-${Math.random()}-${index}`,
              serviceId: service.value,
              serviceName: service.label,
              packageId: null,
              packageName: null,
              membershipId: null,
              invoiceDetailId: null, // Regular standalone service, not from invoice
              employeeId: calendarContext.employeeId || null, // Pre-select employee from calendar
              roomId: null,
              equipmentId: null,
              fromTime: fromTime,
              toTime: toTime
            };
          });

          // Mark that first service has been added
          if (calendarContext.isFirstService) {
            setCalendarContext(prev => ({ ...prev, isFirstService: false }));
          }

          // Combine: package/membership resources + standalone billed services + preserved regular services + new services
          const updatedResources = [
            ...packageMembershipResources,
            ...standaloneBilledResources, // Preserve standalone billed services
            ...preservedResources,
            ...newResources
          ];

          // COMMENTED OUT per user request - availability checks not needed
          // Trigger availability checks after resources are added
          // setTimeout(() => {
          //   updatedResources.forEach(resource => {
          //     if (resource.fromTime && resource.toTime) {
          //       checkTimeAvailability(resource.id, resource.fromTime, resource.toTime);
          //     }
          //   });
          //   checkAvailabilityForSelection();
          // }, 500);

          return updatedResources;
        });
      } else {
        // Don't remove service resources when all services are cleared from dropdown
        // They can only be removed via the Remove button in Service Resource Allocation table
        // Keep all service resources
        // setServiceResources(prev => prev.filter(resource => 
        //   resource.packageId || resource.membershipId || resource.invoiceDetailId
        // ));
      }
    }

    if (field === 'booking_Date') {
      // Note: Availability checks are handled by useEffect hook when booking_Date changes
      // No need to manually trigger here to avoid duplicate API calls
      // The useEffect will automatically refresh availability when date changes
    }
  };

  const handleResourceChange = (resourceId, field, value) => {
    let updatedResource = null;

    setServiceResources(prev => {
      const updated = prev.map(resource => {
        if (resource.id === resourceId) {
          updatedResource = { ...resource, [field]: value || null };
          return updatedResource;
        }
        return resource;
      });
      return updated;
    });

    // COMMENTED OUT per user request - availability checks not needed
    // Check time-based availability when time changes
    // if (['fromTime', 'toTime'].includes(field) && updatedResource) {
    //   // Use the updated resource directly instead of reading from state
    //   const fromTime = field === 'fromTime' ? value : updatedResource.fromTime;
    //   const toTime = field === 'toTime' ? value : updatedResource.toTime;
    //   
    //   if (fromTime && toTime) {
    //     setCheckingTimeAvailability(true);
    //     setTimeout(() => {
    //       checkTimeAvailability(resourceId, fromTime, toTime).finally(() => {
    //         setCheckingTimeAvailability(false);
    //         // Also trigger bulk availability check to refresh all dropdowns
    //         checkAvailabilityForSelection();
    //       });
    //     }, 500);
    //   }
    // }
    // 
    // // Check availability when employee or room changes
    // if (['employeeId', 'roomId'].includes(field)) {
    //   setTimeout(() => {
    //     checkAvailabilityForSelection();
    //   }, 500);
    // COMMENTED OUT per user request - availability checks not needed
    // ...
  };

  const handleFromTimeChange = (resourceId, newFromTime) => {
    if (!newFromTime) return;

    setServiceResources(prev => {
      const index = prev.findIndex(r => r.id === resourceId);
      if (index === -1) return prev;

      const updated = [...prev];
      let currentTime = new Date(newFromTime);

      for (let i = index; i < updated.length; i++) {
        const resource = updated[i];
        // Find service duration
        const service = services.find(s => s.value === resource.serviceId);
        const duration = service?.duration || 30;

        updated[i] = {
          ...resource,
          fromTime: new Date(currentTime),
          toTime: new Date(currentTime.getTime() + (duration * 60000))
        };

        // Next resource starts after this one finishes
        currentTime = new Date(updated[i].toTime);
      }

      // Sort the entire list to keep it chronological
      updated.sort((a, b) => {
        if (!a.fromTime) return 1;
        if (!b.fromTime) return -1;
        return a.fromTime.getTime() - b.fromTime.getTime();
      });

      return updated;
    });
  };

  const handleRemoveService = (resourceId) => {
    setServiceResources(prev => prev.filter(resource => resource.id !== resourceId));
  };

  // Build membership table data (includes both membership services and billed standalone services)
  const buildMembershipTableData = () => {
    const membershipData = [];

    // 1. Process membership services - NOW GROUPED BY MEMBERSHIP
    if (allCustomerMemberships && allCustomerMemberships.length > 0) {
      // Count how many instances of each membership service are already added to serviceResources
      // IMPORTANT: Only count membership services (with membershipId), NOT standalone services
      // Updated to track by membershipId as well
      const addedServiceCounts = {};
      serviceResources
        .filter(r => r.membershipId) // Only count membership services (exclude standalone billed services)
        .forEach(r => {
          const serviceId = r.serviceId;
          const packageId = r.packageId || null;
          const membershipId = r.membershipId;
          // Create composite key to track service+package+membership combination
          const compositeKey = packageId
            ? `${membershipId}_${serviceId}_${packageId}`
            : `${membershipId}_${serviceId}_direct`;
          addedServiceCounts[compositeKey] = (addedServiceCounts[compositeKey] || 0) + 1;
        });

      // Map to store service data grouped by membership
      // Key format: membershipId_serviceId_packageId or membershipId_serviceId_direct
      const serviceMap = new Map();

      // Process all memberships - NOW SEPARATING BY MEMBERSHIP
      allCustomerMemberships.forEach(membership => {
        if (!membership || !membership.details || membership.details.length === 0) return;

        // Get purchase date from membership
        const purchaseDate = membership.purchaseDate || membership.PurchaseDate;
        const membershipId = membership.membershipId;
        const entityType = membership.entity_Type?.toUpperCase();
        const entityId = membership.entity_Id || membership.entityId; // SeriesId for package series

        // Get series name if this is a package series membership
        let seriesName = null;
        if (entityType === 'SEP' && entityId) {
          const series = packageSeries.find(s => s.value === entityId);
          if (series) {
            // packageSeries is formatted with label property (from loadPackageSeries)
            seriesName = series.label || series.seriesName_En || series.SeriesName_En || `Series ${entityId}`;
          }
        }

        // Filter only pending details and exclude products
        // UPDATED: Use RemainingQty from API instead of counting manually
        const pendingDetails = membership.details.filter(detail =>
          detail.completionStatus === "Pending" &&
          detail.serviceId &&
          !detail.productId && // Exclude products
          (detail.remainingQty || detail.RemainingQty || 0) > 0 // Only include if RemainingQty > 0
        );

        if (pendingDetails.length === 0) return;

        // Process each pending service detail - GROUPED BY MEMBERSHIP
        pendingDetails.forEach(detail => {
          const serviceId = detail.serviceId;
          const packageId = detail.packageId || null;
          // Use PurchaseDate from detail if available, otherwise from membership
          const detailPurchaseDate = detail.purchaseDate || detail.PurchaseDate || purchaseDate;
          // Get RemainingQty from API (backend calculated)
          const remainingQty = detail.remainingQty || detail.RemainingQty || 0;

          // Get service name
          const service = services.find(s => s.value === serviceId);
          if (!service) return;

          // Get package name if available
          let packageName = null;
          if (packageId) {
            const pkg = packages.find(p => p.value === packageId);
            packageName = pkg ? pkg.label : null;
          }

          // Format package name: For package series, show "SeriesName/PackageName" or "SeriesName/Direct Service"
          let formattedPackageName = null;
          if (entityType === 'SEP' && seriesName) {
            if (packageName) {
              formattedPackageName = `${seriesName}/${packageName}`;
            } else {
              formattedPackageName = `${seriesName}/Direct Service`;
            }
          } else {
            // For regular packages, just use package name or "Direct Service"
            formattedPackageName = packageName || 'Direct Service';
          }

          // Create composite key: membershipId_serviceId_packageId (group by membership)
          const compositeKey = packageId
            ? `${membershipId}_${serviceId}_${packageId}`
            : `${membershipId}_${serviceId}_direct`;

          // Aggregate service data by membership (not across all memberships)
          // UPDATED: Use RemainingQty from API instead of counting
          if (!serviceMap.has(compositeKey)) {
            serviceMap.set(compositeKey, {
              serviceId: serviceId,
              serviceName: service.label,
              packageId: packageId,
              packageName: formattedPackageName, // Use formatted name with series
              membershipId: membershipId,
              purchaseDate: detailPurchaseDate,
              availableQty: remainingQty // Use RemainingQty from API (already accounts for booked services)
            });
          } else {
            // If key already exists, use the maximum RemainingQty (should be same for all details in group)
            const serviceData = serviceMap.get(compositeKey);
            serviceData.availableQty = Math.max(serviceData.availableQty, remainingQty);
          }
        });
      });

      // Convert Map to array and add to membershipData - EACH MEMBERSHIP SEPARATELY
      Array.from(serviceMap.values())
        .forEach(serviceData => {
          const serviceId = serviceData.serviceId;
          const packageId = serviceData.packageId;
          const membershipId = serviceData.membershipId;

          // Use composite key to match the counting logic (includes membershipId)
          const compositeKey = packageId
            ? `${membershipId}_${serviceId}_${packageId}`
            : `${membershipId}_${serviceId}_direct`;
          const alreadyAdded = addedServiceCounts[compositeKey] || 0;
          // UPDATED: serviceData.availableQty already contains RemainingQty from API (accounts for booked services)
          // Still subtract already added services from current session
          const availableQty = Math.max(0, serviceData.availableQty - alreadyAdded);

          // Only include services that still have available quantity
          if (availableQty <= 0) return;

          // Create a unique key for this service+package+membership combination
          const selectionKey = packageId
            ? `${membershipId}_${serviceId}_${packageId}`
            : `${membershipId}_${serviceId}_direct`;

          // Determine membership type
          const membership = allCustomerMemberships.find(m => m.membershipId === membershipId);
          let membershipType = null; // 'package', 'packageSeries', or null
          if (membership) {
            const entityType = membership.entity_Type?.toUpperCase();
            if (entityType === 'PAC') {
              membershipType = 'package';
            } else if (entityType === 'SEP') {
              membershipType = 'packageSeries';
            }
          }

          // Format purchase date for display
          let formattedPurchaseDate = '';
          if (serviceData.purchaseDate) {
            try {
              const date = new Date(serviceData.purchaseDate);
              if (!isNaN(date.getTime())) {
                formattedPurchaseDate = date.toLocaleDateString('en-GB', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric'
                });
              }
            } catch (e) {
              console.warn('Error formatting purchase date:', e);
            }
          }

          membershipData.push({
            serviceId: serviceId,
            serviceName: serviceData.serviceName,
            availableQty: availableQty,
            packageNames: serviceData.packageName || 'Direct Service', // Already formatted with series name if applicable
            packageId: packageId,
            membershipId: membershipId,
            membershipIds: [membershipId], // Single membership per row now
            purchaseDate: serviceData.purchaseDate,
            formattedPurchaseDate: formattedPurchaseDate,
            selectionKey: selectionKey,
            membershipType: membershipType,
            isBilledService: false
          });
        });
    }

    // 2. Process billed standalone services
    if (billedStandaloneServices && billedStandaloneServices.length > 0) {
      // Count how many instances of each billed service are already added
      const addedBilledServiceCounts = {};
      serviceResources
        .filter(r => r.invoiceDetailId) // Only count billed services
        .forEach(r => {
          const detailId = r.invoiceDetailId;
          addedBilledServiceCounts[detailId] = (addedBilledServiceCounts[detailId] || 0) + 1;
        });

      billedStandaloneServices.forEach(billedService => {
        // Handle both camelCase and PascalCase from API
        const serviceId = billedService.Service_Id || billedService.service_Id;
        const detailId = billedService.Detail_Id || billedService.detail_Id;
        const alreadyAdded = addedBilledServiceCounts[detailId] || 0;
        const remainingQty = Math.max(0, (billedService.RemainingQuantity || billedService.remainingQuantity || 0) - alreadyAdded);

        // Only include services with remaining quantity > 0
        if (remainingQty > 0) {
          // Extract purchase date (Invoice_Date) from billed service
          const invoiceDate = billedService.Invoice_Date || billedService.invoice_Date;

          // Format purchase date for display
          let formattedPurchaseDate = '';
          if (invoiceDate) {
            try {
              const date = new Date(invoiceDate);
              if (!isNaN(date.getTime())) {
                formattedPurchaseDate = date.toLocaleDateString('en-GB', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric'
                });
              }
            } catch (e) {
              console.warn('Error formatting purchase date for billed service:', e);
            }
          }

          const service = services.find(s => s.value === serviceId);
          if (service) {
            const selectionKey = `billed_${detailId}`;
            membershipData.push({
              serviceId: serviceId,
              serviceName: service.label,
              availableQty: Math.floor(remainingQty), // Convert to integer
              packageNames: 'Standalone Service',
              packageId: null,
              membershipIds: [],
              selectionKey: selectionKey,
              membershipType: null, // Standalone service
              isBilledService: true, // Flag to identify billed services
              invoiceDetailId: detailId,
              invoiceId: billedService.Invoice_Id || billedService.invoice_Id,
              invoiceNo: billedService.Invoice_No || billedService.invoice_No,
              invoiceSourceId: billedService.Invoice_SourceId || billedService.invoice_SourceId,
              duration: (billedService.Duration || billedService.duration || service.duration || 30),
              purchaseDate: invoiceDate,
              formattedPurchaseDate: formattedPurchaseDate
            });
          } else {
            // Service not found in services list - use API data directly
            console.warn(`Service ${serviceId} not found in services list, using API data`);
            const serviceName = billedService.SpaServiceName_En || billedService.spaServiceName_En ||
              billedService.SpaServiceName_Ar || billedService.spaServiceName_Ar ||
              `Service ${serviceId}`;
            const selectionKey = `billed_${detailId}`;
            membershipData.push({
              serviceId: serviceId,
              serviceName: serviceName,
              availableQty: Math.floor(remainingQty),
              packageNames: 'Standalone Service',
              packageId: null,
              membershipIds: [],
              selectionKey: selectionKey,
              membershipType: null,
              isBilledService: true,
              invoiceDetailId: detailId,
              invoiceId: billedService.Invoice_Id || billedService.invoice_Id,
              invoiceNo: billedService.Invoice_No || billedService.invoice_No,
              invoiceSourceId: billedService.Invoice_SourceId || billedService.invoice_SourceId,
              duration: (billedService.Duration || billedService.duration || 30),
              purchaseDate: invoiceDate,
              formattedPurchaseDate: formattedPurchaseDate
            });
          }
        }
      });
    }

    // Sort: Group by Purchase Date first (older first), then by service name within each date
    return membershipData.sort((a, b) => {
      // Billed services go last
      if (a.isBilledService && !b.isBilledService) return 1;
      if (!a.isBilledService && b.isBilledService) return -1;

      // Get purchase dates for both items
      const dateA = a.purchaseDate ? new Date(a.purchaseDate) : null;
      const dateB = b.purchaseDate ? new Date(b.purchaseDate) : null;

      // If both have valid dates, sort by date (older first)
      if (dateA && dateB && !isNaN(dateA.getTime()) && !isNaN(dateB.getTime())) {
        const dateDiff = dateA - dateB;
        if (dateDiff !== 0) {
          return dateDiff; // Older dates first
        }
      } else if (dateA && !dateB) {
        return -1; // a has date, b doesn't - a comes first
      } else if (!dateA && dateB) {
        return 1; // b has date, a doesn't - b comes first
      }

      // If same date (or both missing dates), sort by service name
      return a.serviceName.localeCompare(b.serviceName);
    });
  };

  const openMembershipModal = (force = false) => {
    // Open modal if customer has memberships OR billed services
    const hasMemberships = hasMembership && allCustomerMemberships.length > 0;
    const hasBilledServices = billedStandaloneServices && billedStandaloneServices.length > 0;

    if (force || hasMemberships || hasBilledServices) {
      setShowMembershipModal(true);
      // Reset selections when opening modal
      setSelectedMembershipServices([]);
      setSelectedMembershipPackages([]);
      setSelectedMembershipPackageSeries([]);
      // Reset table selection
      setTableSelection({});
    }
  };

  const closeMembershipModal = () => {
    setShowMembershipModal(false);
    setSelectedMembershipServices([]);
    setSelectedMembershipPackages([]);
    setSelectedMembershipPackageSeries([]);
    // Reset table selection
    setTableSelection({});
  };

  // Handle table-based service selection
  const handleTableServiceSelection = (selectionKey, checked) => {
    setTableSelection(prev => {
      const newSelection = { ...prev };
      if (checked) {
        // Get service data to set default required quantity
        const tableData = buildMembershipTableData();
        const serviceData = tableData.find(s => s.selectionKey === selectionKey);
        if (serviceData) {
          newSelection[selectionKey] = {
            selected: true,
            requiredQty: 1, // Default to 1
            membershipIds: serviceData.membershipIds || [serviceData.membershipId].filter(Boolean)
          };
        }
      } else {
        // Remove selection
        delete newSelection[selectionKey];
      }
      return newSelection;
    });
  };

  // Handle required quantity change
  const handleRequiredQtyChange = (selectionKey, qty) => {
    setTableSelection(prev => {
      const newSelection = { ...prev };
      if (newSelection[selectionKey]) {
        const tableData = buildMembershipTableData();
        const serviceData = tableData.find(s => s.selectionKey === selectionKey);
        const availableQty = serviceData ? serviceData.availableQty : 0;

        // Allow empty string for deletion, otherwise parse the value
        if (qty === '' || qty === null || qty === undefined) {
          newSelection[selectionKey] = {
            ...newSelection[selectionKey],
            requiredQty: ''
          };
        } else {
          const requestedQty = parseInt(qty);
          if (!isNaN(requestedQty) && requestedQty > 0) {
            newSelection[selectionKey] = {
              ...newSelection[selectionKey],
              requiredQty: Math.min(requestedQty, availableQty)
            };
          } else {
            newSelection[selectionKey] = {
              ...newSelection[selectionKey],
              requiredQty: ''
            };
          }
        }
      }
      return newSelection;
    });
  };

  // Apply membership selections from table (includes both membership and billed services)
  const applyMembershipSelections = async () => {
    const selectedServices = [];
    const tableData = buildMembershipTableData();

    // Process table selections
    Object.keys(tableSelection).forEach(selectionKey => {
      const selection = tableSelection[selectionKey];
      if (selection && selection.selected) {
        const serviceData = tableData.find(s => s.selectionKey === selectionKey);
        if (serviceData) {
          const service = services.find(s => s.value === serviceData.serviceId);
          if (service) {
            // Get required quantity (default to 1 if missing)
            let requiredQty = selection.requiredQty;
            if (!requiredQty || requiredQty === '' || requiredQty === 0) {
              requiredQty = 1;
            }
            requiredQty = Math.min(requiredQty, serviceData.availableQty);

            // Check if this is a billed service
            if (serviceData.isBilledService && serviceData.invoiceDetailId) {
              // Handle billed standalone services
              for (let i = 0; i < requiredQty; i++) {
                selectedServices.push({
                  ...service,
                  membershipId: null,
                  packageId: null,
                  detailId: null,
                  invoiceDetailId: serviceData.invoiceDetailId, // Link to invoice detail
                  invoiceSourceId: serviceData.invoiceSourceId, // Store source for form population
                  duration: serviceData.duration || service.duration || 30
                });
              }
            } else {
              // Handle membership services - NOW WORKING WITH SINGLE MEMBERSHIP PER ROW
              // serviceData.membershipId contains the specific membership ID for this row
              const membershipId = serviceData.membershipId;
              const membership = allCustomerMemberships.find(m => m.membershipId === membershipId);

              if (membership && membership.details) {
                // Filter details for this specific membership, service, and package
                const pendingDetails = membership.details.filter((detail) => (
                  detail.serviceId === serviceData.serviceId &&
                  detail.completionStatus === "Pending" &&
                  !detail.productId &&
                  (serviceData.packageId === null ? detail.packageId === null : detail.packageId === serviceData.packageId)
                ));

                // Add services up to required quantity from this specific membership
                for (let i = 0; i < requiredQty && i < pendingDetails.length; i++) {
                  const detail = pendingDetails[i];
                  selectedServices.push({
                    ...service,
                    membershipId: detail.membershipId,
                    packageId: detail.packageId,
                    detailId: detail.detailId || null,
                    invoiceDetailId: null // No invoice link for membership services
                  });
                }
              }
            }
          }
        }
      }
    });

    // Update form's source_Id if a billed service was selected
    const billedServiceWithSource = selectedServices.find(s => s.invoiceSourceId);
    if (billedServiceWithSource && billedServiceWithSource.invoiceSourceId) {
      setForm(prev => ({
        ...prev,
        source_Id: billedServiceWithSource.invoiceSourceId
      }));
    }

    // Update service resources for membership selections
    const newServiceResources = [...serviceResources];

    // Calculate time slots for membership services - use calendar start time if available
    const bookingDate = new Date(form.booking_Date);
    let baseTime = new Date(bookingDate);
    baseTime.setHours(9, 0, 0, 0);

    let useCalendarStartTime = false;
    if (calendarContext.isFirstService && calendarContext.startTime) {
      const calendarStart = new Date(calendarContext.startTime);
      if (calendarStart.getFullYear() === bookingDate.getFullYear() &&
        calendarStart.getMonth() === bookingDate.getMonth() &&
        calendarStart.getDate() === bookingDate.getDate()) {
        baseTime = new Date(calendarStart);
        useCalendarStartTime = true;
        console.log('Using calendar start time for membership services:', baseTime);
      }
    }

    // If not using calendar start time, and there are already resources, start after the last one to ensure sequential timing
    if (!useCalendarStartTime && serviceResources.length > 0) {
      // Find the latest end time among existing resources
      let latestResEndTime = baseTime;
      serviceResources.forEach(resource => {
        if (resource.toTime) {
          const resourceToTime = new Date(resource.toTime);
          if (resourceToTime > latestResEndTime) {
            latestResEndTime = resourceToTime;
          }
        }
      });
      baseTime = latestResEndTime;
    }

    let cumulativeMinutes = 0;

    // Add selected services to service resources
    selectedServices.forEach(service => {
      // Create unique resource ID based on service type
      const resourceId = service.invoiceDetailId
        ? `billed-srv-${service.serviceId}-${service.invoiceDetailId}-${Date.now()}`
        : `mem-srv-${service.serviceId}-${service.membershipId}-${service.detailId || Date.now()}`;

      // Check if this exact service instance is already added
      const isDuplicate = service.invoiceDetailId
        ? newServiceResources.find(r =>
          r.serviceId === service.value &&
          r.invoiceDetailId === service.invoiceDetailId
        )
        : newServiceResources.find(r =>
          r.serviceId === service.value &&
          r.membershipId === service.membershipId &&
          r.detailId === service.detailId
        );

      if (!isDuplicate) {
        const packageId = service.packageId || null;
        const packageName = packageId ? packages.find(p => p.value === packageId)?.label : null;

        // Get service duration
        const serviceFromList = services.find(s => s.value === service.value);
        const duration = service.duration || serviceFromList?.duration || 30;

        // Calculate time slots using service duration
        const fromTime = new Date(baseTime.getTime() + (cumulativeMinutes * 60000));
        const toTime = new Date(fromTime.getTime() + (duration * 60000));

        newServiceResources.push({
          id: resourceId,
          serviceId: service.value,
          serviceName: service.label,
          packageId: packageId,
          packageName: packageName,
          membershipId: service.membershipId,
          detailId: service.detailId,
          invoiceDetailId: service.invoiceDetailId || null, // Add invoice detail ID for billed services
          employeeId: calendarContext.employeeId || null, // Pre-select employee from calendar
          roomId: null,
          equipmentId: null,
          fromTime: fromTime,
          toTime: toTime
        });

        // Update cumulative minutes for next service
        cumulativeMinutes += duration;
      }
    });

    setServiceResources(newServiceResources);

    // Mark that first service has been added
    if (calendarContext.isFirstService) {
      setCalendarContext(prev => ({ ...prev, isFirstService: false }));
    }

    closeMembershipModal();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.customer_Id || !form.source_Id || !form.business_Id || !form.branch_Id) {
      Swal.fire("Validation Error", "Please fill all required fields (Customer, Source, Company, and Branch)", "error");
      return;
    }

    if (serviceResources.length === 0) {
      Swal.fire("Validation Error", "Please select at least one service or package", "error");
      return;
    }

    // Validate that all selected services and packages are active
    const inactiveEntities = [];

    // Check selected services
    if (form.selectedServices && form.selectedServices.length > 0) {
      for (const service of form.selectedServices) {
        const serviceData = services.find(s => s.value === service.value);
        if (!serviceData) {
          // Try to find in original data if not in formatted list
          // This shouldn't happen if filtering is working, but add as safety check
          inactiveEntities.push({ name: service.label || `Service ${service.value}`, type: 'Service' });
        }
      }
    }

    // Check selected packages
    if (form.selectedPackages && form.selectedPackages.length > 0) {
      for (const pkg of form.selectedPackages) {
        const packageData = packages.find(p => p.value === pkg.value);
        if (!packageData) {
          inactiveEntities.push({ name: pkg.label || `Package ${pkg.value}`, type: 'Package' });
        }
      }
    }

    // Check service resources (which may include services from packages)
    for (const resource of serviceResources) {
      if (resource.serviceId) {
        const serviceData = services.find(s => s.value === resource.serviceId);
        if (!serviceData) {
          inactiveEntities.push({ name: resource.serviceName || `Service ${resource.serviceId}`, type: 'Service' });
        }
      }
    }

    if (inactiveEntities.length > 0) {
      const entityList = inactiveEntities.map(e => `${e.type}: ${e.name}`).join('\n');
      Swal.fire("Validation Error", `Cannot book inactive entities. Please remove the following inactive items:\n\n${entityList}`, "error");
      return;
    }

    for (const resource of serviceResources) {
      if (resource.fromTime && resource.toTime) {
        if (new Date(resource.toTime) <= new Date(resource.fromTime)) {
          Swal.fire("Validation Error", `To time must be after from time for service: ${resource.serviceName}`, "error");
          return;
        }
      }
    }

    try {
      setLoading(true);

      const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
      const currentUserId = authUser.userId || authUser.user_Id || 1;

      const details = [];
      const resourceDetails = [];
      let detailIndex = 1;

      const individualServices = serviceResources.filter(r => !r.packageId && !r.membershipId);
      const packageServices = serviceResources.filter(r => r.packageId && !r.membershipId);
      const membershipServices = serviceResources.filter(r => r.membershipId);

      const uniquePackages = [...new Set(packageServices.map(r => r.packageId))];
      const uniqueMemberships = [...new Set(membershipServices.map(r => r.membershipId))];

      if (individualServices.length > 0) {
        // Group services by serviceId to handle multiple instances of the same service
        const servicesByServiceId = {};
        individualServices.forEach(service => {
          if (!servicesByServiceId[service.serviceId]) {
            servicesByServiceId[service.serviceId] = [];
          }
          servicesByServiceId[service.serviceId].push(service);
        });

        // Create a detail entry for each service instance
        Object.keys(servicesByServiceId).forEach(serviceId => {
          const serviceInstances = servicesByServiceId[serviceId];

          serviceInstances.forEach(service => {
            const detailId = detailIndex++;
            const detailObj = {
              tempDetailId: detailId,
              entity_Id: service.serviceId,
              entity_Type: 'ser',
              is_Cancelled: false,
              cancel_Reason: null,
              created_By: currentUserId,
              created_On: new Date().toISOString(),
              modified_By: currentUserId,
              modified_On: new Date().toISOString(),
            };
            details.push(detailObj);

            const bookingDate = new Date(form.booking_Date);
            const fromTime = service.fromTime ? new Date(bookingDate.getFullYear(), bookingDate.getMonth(), bookingDate.getDate(),
              service.fromTime.getHours(), service.fromTime.getMinutes(), service.fromTime.getSeconds()) : null;
            const toTime = service.toTime ? new Date(bookingDate.getFullYear(), bookingDate.getMonth(), bookingDate.getDate(),
              service.toTime.getHours(), service.toTime.getMinutes(), service.toTime.getSeconds()) : null;

            const resourceDetailObj = {
              tempDetailId: detailId,
              service_Id: service.serviceId,
              package_Id: null,
              membership_Id: null,
              room_Id: service.roomId,
              equipment_Id: service.equipmentId,
              employee_Id: service.employeeId,
              from_Time: fromTime ? formatLocalDateTime(fromTime) : null,
              to_Time: toTime ? formatLocalDateTime(toTime) : null,
              modified_By: currentUserId,
              modified_On: new Date().toISOString(),
              completionStatus: null,
              cancellation_Reason: null,
              InvoiceDetail_Id: service.invoiceDetailId || null, // Add invoice detail ID for billed services
            };
            resourceDetails.push(resourceDetailObj);
          });
        });
      }

      if (uniquePackages.length > 0) {
        uniquePackages.forEach(packageId => {
          let servicesInPackage = packageServices.filter(r => r.packageId === packageId);

          // Ensure all services have packageInstanceId initialized
          // If missing, assign based on time grouping (for backward compatibility)
          const bookingDate = new Date(form.booking_Date);
          let currentInstanceId = 1;
          let lastFromTime = null;

          // Sort by fromTime first to group services with similar times
          const sortedServices = [...servicesInPackage].sort((a, b) => {
            const aTime = a.fromTime ? new Date(bookingDate.getFullYear(), bookingDate.getMonth(), bookingDate.getDate(),
              a.fromTime.getHours(), a.fromTime.getMinutes(), a.fromTime.getSeconds()) : new Date(0);
            const bTime = b.fromTime ? new Date(bookingDate.getFullYear(), bookingDate.getMonth(), bookingDate.getDate(),
              b.fromTime.getHours(), b.fromTime.getMinutes(), b.fromTime.getSeconds()) : new Date(0);
            return aTime.getTime() - bTime.getTime();
          });

          servicesInPackage = sortedServices.map((service) => {
            if (service.packageInstanceId === undefined || service.packageInstanceId === null) {
              const serviceFromTime = service.fromTime ? new Date(bookingDate.getFullYear(), bookingDate.getMonth(), bookingDate.getDate(),
                service.fromTime.getHours(), service.fromTime.getMinutes(), service.fromTime.getSeconds()) : null;

              // Start new instance if time gap is large (more than 1 hour)
              if (lastFromTime && serviceFromTime && (serviceFromTime.getTime() - lastFromTime.getTime() > 60 * 60 * 1000)) {
                currentInstanceId++;
              }

              if (serviceFromTime) {
                lastFromTime = serviceFromTime;
              }

              return { ...service, packageInstanceId: currentInstanceId };
            }
            return service;
          });

          // Group services into package instances using packageInstanceId
          const packageInstances = [];

          // Group by packageInstanceId - ensure we capture ALL services
          const instancesMap = {};
          servicesInPackage.forEach(service => {
            // Ensure packageInstanceId is set - use the service's packageInstanceId or default to 1
            const instanceId = (service.packageInstanceId !== undefined && service.packageInstanceId !== null)
              ? service.packageInstanceId
              : 1;

            if (!instancesMap[instanceId]) {
              instancesMap[instanceId] = [];
            }
            instancesMap[instanceId].push(service);
          });

          // Sort instance IDs and create instances array
          Object.keys(instancesMap).sort((a, b) => parseInt(a) - parseInt(b)).forEach(instanceId => {
            packageInstances.push(instancesMap[instanceId]);
          });

          // Fallback: if no instances created, treat all as one instance
          if (packageInstances.length === 0 && servicesInPackage.length > 0) {
            packageInstances.push(servicesInPackage);
          }

          // Verify: Log to ensure all services are included (can be removed after debugging)
          const totalServicesInInstances = packageInstances.reduce((sum, inst) => sum + inst.length, 0);
          if (totalServicesInInstances !== servicesInPackage.length) {
            console.warn(`⚠️ Package ${packageId}: Mismatch! Total services: ${servicesInPackage.length}, In instances: ${totalServicesInInstances}`);
            console.warn('Services in package:', servicesInPackage.map(s => ({
              serviceId: s.serviceId,
              serviceName: s.serviceName,
              packageInstanceId: s.packageInstanceId
            })));
          }

          // Create a detail entry for each package instance
          packageInstances.forEach(instanceServices => {
            const detailId = detailIndex++;
            const packageDetailObj = {
              tempDetailId: detailId,
              entity_Id: packageId,
              entity_Type: 'pac',
              is_Cancelled: false,
              cancel_Reason: null,
              created_By: currentUserId,
              created_On: new Date().toISOString(),
              modified_By: currentUserId,
              modified_On: new Date().toISOString(),
            };
            details.push(packageDetailObj);

            // Include ALL services in this instance - don't deduplicate
            // Each service resource should be included as it represents a unique allocation
            const uniqueServicesInInstance = instanceServices;

            uniqueServicesInInstance.forEach(service => {
              const bookingDate = new Date(form.booking_Date);
              const fromTime = service.fromTime ? new Date(bookingDate.getFullYear(), bookingDate.getMonth(), bookingDate.getDate(),
                service.fromTime.getHours(), service.fromTime.getMinutes(), service.fromTime.getSeconds()) : null;
              const toTime = service.toTime ? new Date(bookingDate.getFullYear(), bookingDate.getMonth(), bookingDate.getDate(),
                service.toTime.getHours(), service.toTime.getMinutes(), service.toTime.getSeconds()) : null;

              const packageResourceDetailObj = {
                tempDetailId: detailId,
                service_Id: service.serviceId,
                package_Id: packageId,
                membership_Id: null,
                room_Id: service.roomId,
                equipment_Id: service.equipmentId,
                employee_Id: service.employeeId,
                from_Time: formatLocalDateTime(fromTime),
                to_Time: formatLocalDateTime(toTime),
                modified_By: currentUserId,
                modified_On: new Date().toISOString(),
                completionStatus: null,
                cancellation_Reason: null,
                InvoiceDetail_Id: service.invoiceDetailId || null,
              };
              resourceDetails.push(packageResourceDetailObj);
            });
          });
        });
      }

      if (uniqueMemberships.length > 0) {
        uniqueMemberships.forEach(membershipId => {
          const detailId = detailIndex++;
          const membershipDetailObj = {
            tempDetailId: detailId,
            entity_Id: membershipId,
            entity_Type: 'mem',
            is_Cancelled: false,
            cancel_Reason: null,
            created_By: currentUserId,
            created_On: new Date().toISOString(),
            modified_By: currentUserId,
            modified_On: new Date().toISOString(),
          };
          details.push(membershipDetailObj);

          const servicesInMembership = membershipServices.filter(r => r.membershipId === membershipId);
          servicesInMembership.forEach(service => {
            const bookingDate = new Date(form.booking_Date);
            const fromTime = service.fromTime ? new Date(bookingDate.getFullYear(), bookingDate.getMonth(), bookingDate.getDate(),
              service.fromTime.getHours(), service.fromTime.getMinutes(), service.fromTime.getSeconds()) : null;
            const toTime = service.toTime ? new Date(bookingDate.getFullYear(), bookingDate.getMonth(), bookingDate.getDate(),
              service.toTime.getHours(), service.toTime.getMinutes(), service.toTime.getSeconds()) : null;

            const membershipResourceDetailObj = {
              tempDetailId: detailId,
              service_Id: service.serviceId,
              package_Id: service.packageId,
              membership_Id: membershipId,
              room_Id: service.roomId,
              equipment_Id: service.equipmentId,
              employee_Id: service.employeeId,
              from_Time: formatLocalDateTime(fromTime),
              to_Time: formatLocalDateTime(toTime),
              modified_By: currentUserId,
              modified_On: new Date().toISOString(),
              completionStatus: null,
              cancellation_Reason: null,
              InvoiceDetail_Id: service.invoiceDetailId || null, // Add invoice detail ID if applicable
            };
            resourceDetails.push(membershipResourceDetailObj);
          });
        });
      }

      // Final deduplication pass on resourceDetails before sending to API
      // This is a safety net to catch any duplicates that might have slipped through
      // IMPORTANT: Include tempDetailId in the key to distinguish between different package/service instances
      const finalDeduplicationKey = new Set();
      const deduplicatedResourceDetails = [];
      resourceDetails.forEach(rd => {
        // Create a key based on all identifying fields INCLUDING tempDetailId to distinguish instances
        const key = `${rd.tempDetailId}_${rd.service_Id}_${rd.package_Id || 'null'}_${rd.membership_Id || 'null'}_${rd.from_Time || 'null'}_${rd.to_Time || 'null'}_${rd.employee_Id || 'null'}_${rd.room_Id || 'null'}_${rd.equipment_Id || 'null'}`;

        if (finalDeduplicationKey.has(key)) {
          console.warn('🚨 Final deduplication: Duplicate resource detail filtered:', {
            tempDetailId: rd.tempDetailId,
            serviceId: rd.service_Id,
            packageId: rd.package_Id,
            fromTime: rd.from_Time,
            toTime: rd.to_Time,
            key: key
          });
          return; // Skip this duplicate
        }
        finalDeduplicationKey.add(key);
        deduplicatedResourceDetails.push(rd);
      });

      if (resourceDetails.length !== deduplicatedResourceDetails.length) {
        // Final deduplication completed
      }

      const bookingDate = new Date(form.booking_Date);
      const allFromTimes = deduplicatedResourceDetails.map(rd => rd.from_Time ? new Date(rd.from_Time) : null).filter(d => d !== null);
      const allToTimes = deduplicatedResourceDetails.map(rd => rd.to_Time ? new Date(rd.to_Time) : null).filter(d => d !== null);

      const earliestFromTime = allFromTimes.length > 0 ? new Date(Math.min(...allFromTimes)) : bookingDate;
      const latestToTime = allToTimes.length > 0 ? new Date(Math.max(...allToTimes)) : bookingDate;

      const bookingData = {
        ...form,
        booking_Date: formatLocalDate(new Date(form.booking_Date)),
        from_Time: formatLocalDateTime(earliestFromTime),
        to_Time: formatLocalDateTime(latestToTime),
        entry_Date: formatLocalDateTime(new Date()),
        source_Id: form.source_Id || null,
        modified_By: currentUserId,
        modified_On: formatLocalDateTime(new Date()),
        sendNotification: form.sendNotification || false,
        business_Id: form.business_Id || 1,
        branch_Id: form.branch_Id || 1,
        details: details,
        resourceDetails: deduplicatedResourceDetails,
      };

      delete bookingData.booking_Id;

      const result = await saveBooking(bookingData);

      if (result.id && result.msg) {
        if (consentData.guestSignature || consentData.witnessSignature) {
          try {
            const consentPayload = {
              booking_Id: result.id,
              witnessName: consentData.witnessName?.trim() || null,
              witnessSignature: consentData.witnessSignature || null,
              guestSignature: consentData.guestSignature || null,
            };
            await signBookingConsent(consentPayload);
          } catch (error) {
            console.error('Error saving consent:', error);
          }
        }

        Swal.fire("Success", result.msg || result.message || "Booking saved successfully", "success").then(() => {
          if (isFromSpaCalendar) {
            navigate('/GmsSpaCalendar');
          } else {
            resetForm();
          }
        });
      } else {
        Swal.fire("Error", result.msg || result.message || "Failed to save booking", "error");
      }
    } catch (error) {
      console.error("Error saving booking:", error);
      Swal.fire("Error", "Failed to save booking", "error");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    // If coming from invoice and user doesn't have company/branch IDs in session, clear them
    // Otherwise, preserve business_Id and branch_Id if user has them in session
    let preservedBusinessId = session.companyId ? (typeof session.companyId === 'string' ? parseInt(session.companyId) : session.companyId) : null;
    let preservedBranchId = session.branchId ? (typeof session.branchId === 'string' ? parseInt(session.branchId) : session.branchId) : null;

    // If we came from invoice and user doesn't have company/branch in session, clear them
    if (isFromInvoice && !session.companyId && !session.branchId) {
      preservedBusinessId = null;
      preservedBranchId = null;
      // Reset invoice-related state
      setIsFromInvoice(false);
      setInvoiceBusinessId(null);
      setInvoiceBranchId(null);
    }

    setForm({
      booking_Id: null,
      customer_Id: null,
      employee_Id: null,
      room_Id: null,
      from_Time: new Date(),
      to_Time: new Date(),
      entry_Date: new Date(),
      booking_Date: new Date(),
      is_Cancelled: false,
      cancellation_Reason: "",
      notes: "",
      source_Id: null,
      sourceOther: "",
      modified_By: null,
      modified_On: null,
      selectedServices: [],
      selectedPackages: [],
      selectedPackageSeries: [],
      business_Id: preservedBusinessId,
      branch_Id: preservedBranchId,
      sendNotification: false,
    });
    setServiceResources([]);
    setSelectedCustomerOption(null);
    setFormKey(prev => prev + 1);

    setCustomerMembership(null);
    setAllCustomerMemberships([]);
    setHasMembership(false);
    setMembershipServices([]);
    setMembershipPackages([]);
    setMembershipPackageSeries([]);

    setShowMembershipModal(false);
    setSelectedMembershipServices([]);
    setSelectedMembershipPackages([]);
    setSelectedMembershipPackageSeries([]);
    setTableSelection({});

    // Clear billed services and billable items status
    setBilledStandaloneServices([]);
    setCustomerHasBillableItems(false);

    setConsentData({
      witnessName: loggedInEmployeeName,
      witnessSignature: '',
      guestSignature: ''
    });
  };

  const handleSignConsent = async (consentPayload) => {
    try {
      setConsentLoading(true);
      setConsentData({
        witnessName: consentPayload.witnessName || '',
        witnessSignature: consentPayload.witnessSignature || '',
        guestSignature: consentPayload.guestSignature || ''
      });
      Swal.fire("Success", "Consent form signed. It will be saved when you save the booking.", "success");
    } catch (error) {
      console.error('Error signing consent:', error);
      Swal.fire("Error", "Failed to sign consent", "error");
    } finally {
      setConsentLoading(false);
    }
  };


  if (!hasPermission) {
    return null;
  }

  return (
    <div className="page-content">
      <Container fluid>
        <Row>
          <Col lg="12">
            <Breadcrumb title="New Booking" breadcrumbItem="Spa" />
            <Card>
              <CardBody>
                <h4 className="card-title mb-4">
                  <Calendar className="me-2" size={20} />
                  New Booking
                </h4>

                <Form onSubmit={handleSubmit}>
                  <Row>
                    <Col md={6}>
                      <FormGroup className="mb-3">
                        <Label for="business_Id">Company <span className="text-danger">*</span></Label>
                        <Select
                          value={businessOptions.find(option => option.value === form.business_Id) || null}
                          onChange={(selected) => {
                            const newBusinessId = selected?.value || null;
                            setForm({
                              ...form,
                              business_Id: newBusinessId,
                              branch_Id: null // Reset branch when business changes
                            });
                          }}
                          options={businessOptions}
                          placeholder="Select Company"
                          styles={selectStyles}
                          isClearable={!isBusinessReadonly}
                          isSearchable
                          required
                          isDisabled={isBusinessReadonly}
                        />
                      </FormGroup>
                    </Col>
                    <Col md={6}>
                      <FormGroup className="mb-3">
                        <Label for="branch_Id">Branch <span className="text-danger">*</span></Label>
                        <Select
                          value={branchOptions.find(option => option.value === form.branch_Id) || null}
                          onChange={(selected) => setForm({ ...form, branch_Id: selected?.value || null })}
                          options={branchOptions}
                          placeholder="Select Branch"
                          styles={selectStyles}
                          isClearable={!isBranchReadonly}
                          isSearchable
                          required
                          isDisabled={isBranchReadonly || !form.business_Id}
                          noOptionsMessage={() => !form.business_Id ? "Please select a company first" : "No branches found"}
                        />
                      </FormGroup>
                    </Col>
                  </Row>

                  <Row>
                    <Col md="6">
                      <FormGroup className="mb-3">
                        <Label for="customer" className="mb-2">
                          Customer *
                          {hasMembership && (
                            <span className="badge bg-success ms-2">
                              <i className="mdi mdi-crown me-1"></i>
                              Membership
                            </span>
                          )}
                        </Label>
                        <div className="d-flex gap-2 align-items-start">
                          <div style={{ flex: 1 }}>
                            <div className="d-flex gap-2">
                              <div style={{ flex: 1 }}>
                                <Select
                                  key={`customer-${formKey}`}
                                  value={selectedCustomerOption}
                                  onChange={(option) => {
                                    setSelectedCustomerOption(option);
                                    handleFormChange('customer_Id', option?.value);

                                    // Default source if customer has LastSource_Id
                                    if (option) {
                                      setForm(prev => ({
                                        ...prev,
                                        source_Id: option.customer?.customer_LastSourceId || null
                                      }));
                                    } else {
                                      setForm(prev => ({
                                        ...prev,
                                        source_Id: null
                                      }));
                                    }
                                  }}
                                  options={customerSearchOptions}
                                  placeholder="Type at least 3 characters to search customers..."
                                  styles={selectStyles}
                                  isClearable
                                  isSearchable
                                  filterOption={() => true} // Disable local filtering as we search via API
                                  isLoading={customerSearchLoading}
                                  noOptionsMessage={() => "Type at least 3 characters to search"}
                                  loadingMessage={() => "Searching customers..."}
                                  onInputChange={(inputValue) => {
                                    if (searchTimeout) {
                                      clearTimeout(searchTimeout);
                                    }
                                    const timeout = setTimeout(() => {
                                      searchCustomersManually(inputValue);
                                    }, 300);
                                    setSearchTimeout(timeout);
                                    return inputValue;
                                  }}
                                />
                              </div>
                              <Button
                                color="info"
                                size="sm"
                                onClick={() => setShowAdvancedSearchModal(true)}
                                title="Advanced Customer Search"
                                style={{ height: '38px' }}
                              >
                                <Search size={16} />
                              </Button>
                              <Button
                                color="success"
                                size="sm"
                                onClick={() => setShowQuickAddCustomer(true)}
                                title="Quick Add Customer"
                                style={{ height: '38px' }}
                              >
                                <i className="fas fa-plus"></i>
                              </Button>
                              {selectedCustomerOption?.value && (
                                <>
                                  <Button
                                    color="info"
                                    size="sm"
                                    onClick={() => setShowCustomerProfile(true)}
                                    title="View Customer Profile"
                                    style={{ height: '38px' }}
                                  >
                                    <User size={16} />
                                  </Button>
                                  <Button
                                    type="button"
                                    color="warning"
                                    size="sm"
                                    onClick={() => setShowConsentModal(true)}
                                    title="Sign Consent Form"
                                    style={{ height: '38px' }}
                                  >
                                    <i className="fas fa-signature"></i>
                                  </Button>
                                </>
                              )}
                              {customerHasBillableItems && (
                                <Button
                                  color="info"
                                  size="sm"
                                  onClick={openMembershipModal}
                                  title="Select Billed Services"
                                  style={{ height: '38px' }}
                                >
                                  <i className="mdi mdi-crown"></i>
                                </Button>
                              )}
                            </div>

                          </div>
                        </div>
                      </FormGroup>
                    </Col>
                    <Col md="6">
                      <FormGroup className="mb-3">
                        <Label for="bookingDate" className="mb-2">Booking Date *</Label>
                        <DatePicker
                          selected={form.booking_Date}
                          onChange={(date) => handleFormChange('booking_Date', date)}
                          className="form-control"
                          dateFormat="dd/MM/yyyy"
                          placeholderText="Select Date"
                        />
                      </FormGroup>
                    </Col>
                  </Row>

                  <Row>
                    <Col md="6">
                      <FormGroup className="mb-3">
                        <Label for="packages" className="mb-2">Packages</Label>
                        <Select
                          key={`packages-${formKey}`}
                          value={form.selectedPackages}
                          onChange={(options, actionMeta) => handleFormChange('selectedPackages', options || [], actionMeta)}
                          options={packages}
                          placeholder="Select Packages"
                          styles={selectStyles}
                          isMulti
                          isClearable={false}
                          isSearchable
                          hideSelectedOptions={false}
                          components={{
                            MultiValueRemove: () => null
                          }}
                        />
                      </FormGroup>
                    </Col>
                    <Col md="6">
                      <FormGroup className="mb-3">
                        <Label for="services" className="mb-2">Services</Label>
                        <Select
                          key={`services-${formKey}`}
                          value={form.selectedServices}
                          onChange={(options, actionMeta) => handleFormChange('selectedServices', options || [], actionMeta)}
                          options={services}
                          placeholder="Select Services"
                          styles={selectStyles}
                          isMulti
                          isClearable={false}
                          isSearchable
                          hideSelectedOptions={false}
                          components={{
                            MultiValueRemove: () => null
                          }}
                        />
                      </FormGroup>
                    </Col>
                  </Row>

                  {serviceResources.length > 0 && (
                    <Row>
                      <Col md="12">
                        <FormGroup className="mb-3">
                          <Label className="mb-2">Service Resource Allocation</Label>
                          <div className="border rounded p-3">
                            <Table responsive striped>
                              <thead>
                                <tr>
                                  <th style={{ width: '25%' }}>Service</th>
                                  <th style={{ width: '12%' }}>From Time</th>
                                  <th style={{ width: '12%' }}>To Time</th>
                                  <th style={{ width: '18%' }}>Employee</th>
                                  <th style={{ width: '15%' }}>Room</th>
                                  <th style={{ width: '18%' }}>Equipment</th>
                                  <th style={{ width: '5%', textAlign: 'center' }}>Remove</th>
                                </tr>
                              </thead>
                              <tbody>
                                {serviceResources.map((resource) => {
                                  // Determine membership type and styling
                                  let membershipType = null;
                                  const isBilledService = resource.invoiceDetailId !== null && resource.invoiceDetailId !== undefined;

                                  if (resource.membershipId) {
                                    const membership = allCustomerMemberships.find(m => m.membershipId === resource.membershipId);
                                    if (membership) {
                                      const entityType = membership.entity_Type?.toUpperCase();
                                      if (entityType === 'PAC') {
                                        membershipType = 'package';
                                      } else if (entityType === 'SEP') {
                                        membershipType = 'packageSeries';
                                      }
                                    }
                                  }

                                  const getRowStyle = () => {
                                    // Priority 1: Standalone billed service (orange)
                                    if (isBilledService) {
                                      return {
                                        backgroundColor: '#fff3e0',
                                        borderLeft: '4px solid #ff9800'
                                      };
                                    }
                                    // Priority 2: Package membership (blue)
                                    if (membershipType === 'package') {
                                      return {
                                        backgroundColor: '#e7f3ff',
                                        borderLeft: '4px solid #0066cc'
                                      };
                                    }
                                    // Priority 3: Package series membership (green)
                                    if (membershipType === 'packageSeries') {
                                      return {
                                        backgroundColor: '#e8f5e9',
                                        borderLeft: '4px solid #2e7d32'
                                      };
                                    }
                                    // Priority 4: Regular package (non-membership)
                                    if (resource.packageId && !resource.membershipId) {
                                      return {
                                        backgroundColor: '#f8f9fa',
                                        borderLeft: '3px solid #007bff'
                                      };
                                    }
                                    return {};
                                  };

                                  const getServiceNameStyle = () => {
                                    // Priority 1: Standalone billed service (orange)
                                    if (isBilledService) {
                                      return { color: '#ff9800', fontWeight: '600' };
                                    }
                                    // Priority 2: Package membership (blue)
                                    if (membershipType === 'package') {
                                      return { color: '#0066cc', fontWeight: '600' };
                                    }
                                    // Priority 3: Package series membership (green)
                                    if (membershipType === 'packageSeries') {
                                      return { color: '#2e7d32', fontWeight: '600' };
                                    }
                                    return {};
                                  };

                                  const getIconClass = () => {
                                    if (isBilledService) {
                                      return 'mdi-receipt text-warning';
                                    } else if (membershipType === 'package') {
                                      return 'mdi-spa text-primary';
                                    } else if (membershipType === 'packageSeries') {
                                      return 'mdi-spa text-success';
                                    }
                                    return 'mdi-spa text-primary';
                                  };

                                  return (
                                    <tr
                                      key={resource.id}
                                      style={getRowStyle()}
                                    >
                                      <td>
                                        <div>
                                          <i className={`mdi ${getIconClass()} me-2`}></i>
                                          <strong style={getServiceNameStyle()}>{resource.serviceName}</strong>
                                          {resource.packageName && (
                                            <div className="text-muted small mt-1">
                                              <i className="mdi mdi-package me-1"></i>
                                              Package: {resource.packageName}
                                            </div>
                                          )}
                                          {resource.membershipId && (
                                            <div className={`small mt-1 ${membershipType === 'package' ? 'text-primary' : membershipType === 'packageSeries' ? 'text-success' : 'text-info'}`}>
                                              <i className="mdi mdi-crown me-1"></i>
                                              {membershipType === 'package' ? 'Package Membership' : membershipType === 'packageSeries' ? 'Package Series Membership' : 'Membership'}
                                            </div>
                                          )}
                                          {isBilledService && (
                                            <div className="small mt-1 text-warning">
                                              <i className="mdi mdi-receipt me-1"></i>
                                              Standalone Billed Service
                                            </div>
                                          )}
                                        </div>
                                      </td>
                                      <td>
                                        <DatePicker
                                          selected={resource.fromTime}
                                          onChange={(date) => {
                                            handleFromTimeChange(resource.id, date);
                                          }}
                                          showTimeSelect
                                          showTimeSelectOnly
                                          timeIntervals={15}
                                          timeCaption="Time"
                                          dateFormat="h:mm aa"
                                          className="form-control"
                                          style={{ width: '100%' }}
                                        />
                                      </td>
                                      <td>
                                        <DatePicker
                                          selected={resource.toTime}
                                          onChange={(date) => {
                                            // Update toTime in state
                                            setServiceResources(prev =>
                                              prev.map(r =>
                                                r.id === resource.id
                                                  ? { ...r, toTime: date }
                                                  : r
                                              )
                                            );

                                            // Then check availability with the updated times
                                            // const updatedResource = { ...resource, toTime: date };
                                            // if (updatedResource.fromTime && updatedResource.toTime) {
                                            //   setCheckingTimeAvailability(true);
                                            //   setTimeout(() => {
                                            //     checkTimeAvailability(resource.id, updatedResource.fromTime, updatedResource.toTime).finally(() => {
                                            //       setCheckingTimeAvailability(false);
                                            //       // Also trigger bulk availability check to refresh all dropdowns
                                            //       checkAvailabilityForSelection();
                                            //     });
                                            //   }, 500);
                                            // }
                                          }}
                                          showTimeSelect
                                          showTimeSelectOnly
                                          timeIntervals={15}
                                          timeCaption="Time"
                                          dateFormat="h:mm aa"
                                          className="form-control"
                                          style={{ width: '100%' }}
                                        />
                                      </td>
                                      <td>
                                        <Select
                                          key={`employee-${resource.id}`}
                                          value={employees.find(emp => emp.value === resource.employeeId)}
                                          onChange={(option) => {
                                            // COMMENTED OUT per user request - availability checks not needed
                                            // const timeAvailability = timeAvailabilityResults[resource.id];
                                            // if (timeAvailability && option?.value) {
                                            //   const employee = timeAvailability.availableEmployees?.find(emp => emp.employee_Id === option.value);
                                            //   if (employee && !employee.isAvailable) {
                                            //     Swal.fire("Warning", "This employee is not available for the selected time slot", "warning");
                                            //     return;
                                            //   }
                                            // }
                                            handleResourceChange(resource.id, 'employeeId', option?.value);
                                          }}
                                          options={formatOptionsWithAvailability(employees, 'Employee', resource.id)}
                                          placeholder="Select"
                                          styles={selectStyles}
                                          isClearable
                                          isSearchable
                                        />
                                      </td>
                                      <td>
                                        <Select
                                          key={`room-${resource.id}`}
                                          value={rooms.find(room => room.value === resource.roomId)}
                                          onChange={(option) => {
                                            // COMMENTED OUT per user request - availability checks not needed
                                            // const timeAvailability = timeAvailabilityResults[resource.id];
                                            // if (timeAvailability && option?.value) {
                                            //   const room = timeAvailability.availableRooms?.find(room => room.room_Id === option.value);
                                            //   if (room && !room.isAvailable) {
                                            //     Swal.fire("Warning", "This room is not available for the selected time slot", "warning");
                                            //     return;
                                            //   }
                                            // }
                                            handleResourceChange(resource.id, 'roomId', option?.value);
                                          }}
                                          options={formatOptionsWithAvailability(rooms, 'Room', resource.id)}
                                          placeholder="Select"
                                          styles={selectStyles}
                                          isClearable
                                          isSearchable
                                        />
                                      </td>
                                      <td>
                                        <Select
                                          value={equipments.find(eq => eq.value === resource.equipmentId)}
                                          onChange={(option) => handleResourceChange(resource.id, 'equipmentId', option?.value)}
                                          options={equipments}
                                          placeholder="Select"
                                          styles={selectStyles}
                                          isClearable
                                          isSearchable
                                        />
                                      </td>
                                      <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                                        <Button
                                          color="danger"
                                          size="sm"
                                          onClick={() => handleRemoveService(resource.id)}
                                          style={{ fontSize: '11px', padding: '4px 8px' }}
                                          title="Remove Service"
                                        >
                                          <X size={14} />
                                        </Button>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </Table>
                          </div>
                        </FormGroup>
                      </Col>
                    </Row>
                  )}

                  <Row>
                    <Col md="6">
                      <FormGroup className="mb-3">
                        <Label for="source" className="mb-2">Source *</Label>
                        <Select
                          key={`source-${formKey}`}
                          value={sourceOptions.find(s => s.value === form.source_Id)}
                          onChange={(option) => handleFormChange('source_Id', option?.value)}
                          options={sourceOptions}
                          placeholder={sourcesLoading ? "Loading sources..." : "Select Source"}
                          styles={selectStyles}
                          isClearable
                          isSearchable
                          isLoading={sourcesLoading}
                          noOptionsMessage={() => sourcesLoading ? "Loading..." : "No sources found"}
                        />
                      </FormGroup>
                    </Col>
                  </Row>

                  <Row>
                    <Col md="12">
                      <FormGroup check className="mb-3 ms-1">
                        <Label check style={{ cursor: 'pointer', fontWeight: '500' }}>
                          <Input
                            type="checkbox"
                            checked={form.sendNotification}
                            onChange={(e) => handleFormChange('sendNotification', e.target.checked)}
                            className="me-2"
                          />
                          Send Notification
                        </Label>
                      </FormGroup>
                    </Col>
                  </Row>

                  <Row>
                    <Col md="12">
                      <FormGroup className="mb-4">
                        <Label for="notes" className="mb-2">Notes</Label>
                        <Input
                          type="textarea"
                          id="notes"
                          value={form.notes}
                          onChange={(e) => handleFormChange('notes', e.target.value)}
                          rows="3"
                          placeholder="Enter any additional notes..."
                        />
                      </FormGroup>
                    </Col>
                  </Row>

                  <Row>
                    <Col md="12">
                      <div className="d-flex gap-2">
                        <Button
                          type="submit"
                          color="primary"
                          disabled={loading}
                        >
                          {loading ? (
                            <ClipLoader size={16} color="#fff" />
                          ) : (
                            "Save Booking"
                          )}
                        </Button>
                        <Button
                          type="button"
                          color="secondary"
                          onClick={resetForm}
                        >
                          Clear
                        </Button>
                        {isFromSpaCalendar && (
                          <Button
                            type="button"
                            color="info"
                            onClick={() => navigate('/GmsSpaCalendar')}
                          >
                            <i className="mdi mdi-calendar-arrow-left me-1"></i>
                            Back to Calendar
                          </Button>
                        )}
                      </div>
                    </Col>
                  </Row>
                </Form>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* Membership Selection Modal - Now includes billed services */}
      <Modal isOpen={showMembershipModal} toggle={closeMembershipModal} size="xl">
        <ModalHeader toggle={closeMembershipModal}>
          <i className="mdi mdi-crown me-2"></i>
          Select Billed Services
        </ModalHeader>
        <ModalBody>
          <div className="mb-3">
            <p className="text-muted">
              Select services from your membership or billed standalone services. Only services with pending completion status (for membership) or available quantity (for billed services) are shown.
              Products are not displayed in this selection.
            </p>
          </div>

          {(() => {
            const tableData = buildMembershipTableData();
            return tableData.length > 0 ? (
              <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
                <Table responsive striped hover className="mb-0">
                  <thead className="table-light" style={{ position: 'sticky', top: 0, zIndex: 10 }}>
                    <tr>
                      <th width="5%" style={{ textAlign: 'center' }}>Select</th>
                      <th width="25%">Service Name</th>
                      <th width="20%">Package Name</th>
                      <th width="15%" style={{ textAlign: 'center' }}>Purchase Date</th>
                      <th width="12%" style={{ textAlign: 'center' }}>Available Qty</th>
                      <th width="18%" style={{ textAlign: 'center' }}>Required Qty</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tableData.map((service) => {
                      // Determine row color based on membership type or billed service
                      const getRowStyle = () => {
                        if (service.isBilledService) {
                          return {
                            backgroundColor: '#fff3e0',
                            borderLeft: '4px solid #ff9800'
                          };
                        } else if (service.membershipType === 'package') {
                          return {
                            backgroundColor: '#e7f3ff',
                            borderLeft: '4px solid #0066cc'
                          };
                        } else if (service.membershipType === 'packageSeries') {
                          return {
                            backgroundColor: '#e8f5e9',
                            borderLeft: '4px solid #2e7d32'
                          };
                        }
                        return {};
                      };

                      const getServiceNameStyle = () => {
                        if (service.isBilledService) {
                          return { color: '#ff9800', fontWeight: '600' };
                        } else if (service.membershipType === 'package') {
                          return { color: '#0066cc', fontWeight: '600' };
                        } else if (service.membershipType === 'packageSeries') {
                          return { color: '#2e7d32', fontWeight: '600' };
                        }
                        return {};
                      };

                      const getIconClass = () => {
                        if (service.isBilledService) {
                          return 'mdi-receipt text-warning';
                        } else if (service.membershipType === 'package') {
                          return 'mdi-spa text-primary';
                        } else if (service.membershipType === 'packageSeries') {
                          return 'mdi-spa text-success';
                        }
                        return 'mdi-spa text-primary';
                      };

                      return (
                        <tr key={service.selectionKey} style={getRowStyle()}>
                          <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                            <div className="form-check">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                id={`service-table-${service.selectionKey}`}
                                checked={tableSelection[service.selectionKey]?.selected || false}
                                onChange={(e) => handleTableServiceSelection(service.selectionKey, e.target.checked)}
                              />
                            </div>
                          </td>
                          <td style={{ verticalAlign: 'middle' }}>
                            <i className={`mdi ${getIconClass()} me-2`}></i>
                            <strong style={getServiceNameStyle()}>{service.serviceName}</strong>
                          </td>
                          <td style={{ verticalAlign: 'middle' }}>
                            <span className="text-muted">{service.packageNames}</span>
                          </td>
                          <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                            {service.formattedPurchaseDate ? (
                              <span className="badge bg-secondary" style={{ fontSize: '12px', padding: '5px 10px' }}>
                                <i className="mdi mdi-calendar me-1"></i>
                                {service.formattedPurchaseDate}
                              </span>
                            ) : (
                              <span className="text-muted">-</span>
                            )}
                          </td>
                          <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                            <span className="badge bg-info" style={{ fontSize: '13px', padding: '6px 12px' }}>
                              {service.availableQty}
                            </span>
                          </td>
                          <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                            {tableSelection[service.selectionKey]?.selected ? (
                              <Input
                                type="number"
                                min="1"
                                max={service.availableQty}
                                value={tableSelection[service.selectionKey]?.requiredQty !== undefined && tableSelection[service.selectionKey]?.requiredQty !== ''
                                  ? tableSelection[service.selectionKey].requiredQty
                                  : ''}
                                onChange={(e) => handleRequiredQtyChange(service.selectionKey, e.target.value)}
                                onBlur={(e) => {
                                  // If empty on blur, set to 1
                                  if (!e.target.value || e.target.value === '') {
                                    handleRequiredQtyChange(service.selectionKey, '1');
                                  }
                                }}
                                style={{ width: '80px', textAlign: 'center' }}
                                className="d-inline-block"
                                placeholder="1"
                              />
                            ) : (
                              <span className="text-muted">-</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            ) : (
              <div className="text-center text-muted py-4">
                <i className="mdi mdi-information-outline me-2"></i>
                No services available for selection.
              </div>
            );
          })()}
        </ModalBody>
        <ModalFooter>
          <div className="d-flex align-items-center gap-3 me-auto" style={{ fontSize: '13px' }}>
            <div className="d-flex align-items-center gap-2">
              <div style={{ width: '16px', height: '16px', backgroundColor: '#e7f3ff', borderLeft: '3px solid #0066cc', borderRadius: '2px' }}></div>
              <span style={{ color: '#0066cc', fontWeight: '500' }}>Package Membership</span>
            </div>
            <div className="d-flex align-items-center gap-2">
              <div style={{ width: '16px', height: '16px', backgroundColor: '#e8f5e9', borderLeft: '3px solid #2e7d32', borderRadius: '2px' }}></div>
              <span style={{ color: '#2e7d32', fontWeight: '500' }}>Package Series Membership</span>
            </div>
            <div className="d-flex align-items-center gap-2">
              <div style={{ width: '16px', height: '16px', backgroundColor: '#fff3e0', borderLeft: '3px solid #ff9800', borderRadius: '2px' }}></div>
              <span style={{ color: '#ff9800', fontWeight: '500' }}>Standalone Billed Service</span>
            </div>
          </div>
          <div>
            <Button color="secondary" onClick={closeMembershipModal}>
              Cancel
            </Button>
            <Button color="primary" onClick={applyMembershipSelections} className="ms-2">
              Apply Selections
            </Button>
          </div>
        </ModalFooter>
      </Modal>

      {/* Quick Add Customer Modal */}
      <QuickAddCustomer
        isOpen={showQuickAddCustomer}
        toggle={() => setShowQuickAddCustomer(false)}
        onCustomerAdded={handleCustomerAdded}
      />

      {/* Customer Profile Modal */}
      <CustomerProfileModal
        isOpen={showCustomerProfile}
        toggle={() => setShowCustomerProfile(false)}
        customerId={selectedCustomerOption?.value || null}
        customer={selectedCustomerOption ? { customer_Id: selectedCustomerOption.value } : null}
      />

      {/* Advanced Customer Search Modal */}
      <CustomerAdvancedSearchModal
        isOpen={showAdvancedSearchModal}
        toggle={() => setShowAdvancedSearchModal(false)}
        userBranchId={session.branchId}
        onCustomerSelected={(customer) => {
          // Set the selected customer in the form and dropdown
          setForm(prev => ({
            ...prev,
            customer_Id: customer.customer_Id,
            source_Id: customer.customer_LastSourceId || null
          }));
          setSelectedCustomerOption({
            value: customer.customer_Id,
            label: `${customer.customer_NameEn || 'N/A'} — ${customer.customer_ContactNo || 'N/A'}`,
            customer: customer
          });
          // Check for membership and billable items
          if (customer.customer_Id) {
            checkCustomerMembership(customer.customer_Id);
          }
        }}
      />

      {/* Consent Form Modal */}
      {selectedCustomerOption?.value && (
        <ConsentFormModal
          isOpen={showConsentModal}
          toggle={() => setShowConsentModal(false)}
          bookingId={0}
          customerName={selectedCustomerOption?.customer?.customer_NameEn || (selectedCustomerOption?.label ? selectedCustomerOption.label.split(' — ')[0] : '') || ''}
          customerMobile={selectedCustomerOption?.customer?.customer_ContactNo || (selectedCustomerOption?.label ? selectedCustomerOption.label.split(' — ')[1] : '') || ''}
          isConsentSigned={false}
          witnessName={consentData.witnessName || loggedInEmployeeName}
          witnessSignature={consentData.witnessSignature}
          guestSignature={consentData.guestSignature}
          onSignConsent={handleSignConsent}
        />
      )}
    </div>
  );
};

export default AddBookingPage;